// DES2 (HC-08 remainder, DES1): the activity step verbs in every app language
// except English, keyed by the presentation registry's tool name. `running` is
// progressive (the line appends “…”), `done` and `failed` match HC-10's line, and
// `counted` is CLDR plural forms with the number where the language puts it.
// The database reads (list_connection_reads, read_connection) live in
// CONNECTION_READ_COPY; a tool missing here reads with the neutral fallback.
import type { ActivityLocale, ActivityVerb } from './activity-verbs';

export const ACTIVITY_VERB_COPY: Record<
  Exclude<ActivityLocale, 'en'>,
  Record<string, ActivityVerb>
> = {
  bg: {
    // Projects v1.
    remove_from_project: {
      running: 'Изваждам от проекта',
      done: 'Извадено от проекта',
      failed: 'Не можа да се извади от проекта',
    },
    project_context: {
      running: 'Чета описанието на проекта',
      done: 'Прочетено описанието на проекта',
      failed: 'Описанието на проекта не можа да бъде прочетено',
    },
    read_projects: {
      running: 'Проверявам проектите',
      done: 'Проверени проекти',
      failed: 'Проектите не можаха да бъдат проверени',
    },
    draft_project: {
      running: 'Подготвям чернова на проекта',
      done: 'Подготвена чернова на проекта',
      failed: 'Черновата на проекта не можа да бъде подготвена',
    },
    update_project: {
      running: 'Подготвям промяната в проекта',
      done: 'Подготвена промяна в проекта',
      failed: 'Промяната в проекта не можа да бъде подготвена',
    },
    move_chat_to_project: {
      running: 'Премествам чата',
      done: 'Чатът е преместен',
      failed: 'Чатът не можа да бъде преместен',
    },
    add_to_project: {
      running: 'Добавям към проекта',
      done: 'Добавено към проекта',
      failed: 'Не можа да се добави към проекта',
    },
    suggest_project_move: {
      running: 'Проверявам къде принадлежи чатът',
      done: 'Предложен е проект',
      failed: 'Не можа да се предложи проект',
    },
    search_company_knowledge: {
      running: 'Търся в знанията на компанията',
      done: 'Претърсени знания на компанията',
      failed: 'Знанията на компанията не можаха да бъдат претърсени',
    },
    read_company_context: {
      running: 'Чета контекста на компанията',
      done: 'Прочетен контекст на компанията',
      failed: 'Контекстът на компанията не можа да бъде прочетен',
    },
    find_company_work: {
      running: 'Преглеждам работата на компанията',
      done: 'Прегледана работа на компанията',
      failed: 'Работата на компанията не можа да бъде прегледана',
    },
    read_company_meeting: {
      running: 'Чета среща',
      done: 'Прочетена среща',
      failed: 'Срещата не можа да бъде прочетена',
      counted: 'one:Прочетена {count} среща|other:Прочетени {count} срещи',
    },
    read_company_file: {
      running: 'Чета файл',
      done: 'Прочетен файл',
      failed: 'Файлът не можа да бъде прочетен',
      counted: 'one:Прочетен {count} файл|other:Прочетени {count} файла',
    },
    search_company_file: {
      running: 'Търся във файл',
      done: 'Претърсен файл',
      failed: 'Файлът не можа да бъде претърсен',
      counted: 'one:Претърсен {count} файл|other:Претърсени {count} файла',
    },
    read_company_source: {
      running: 'Чета документ',
      done: 'Прочетен документ',
      failed: 'Документът не можа да бъде прочетен',
      counted: 'one:Прочетен {count} документ|other:Прочетени {count} документа',
    },
    storefront_stores: {
      running: 'Проверявам асистентите за пазаруване',
      done: 'Проверени асистенти за пазаруване',
      failed: 'Асистентите за пазаруване не можаха да бъдат проверени',
    },
    render_storefront_preview: {
      running: 'Разглеждам асистента за пазаруване',
      done: 'Разгледан асистент за пазаруване',
      failed: 'Асистентът за пазаруване не можа да бъде разгледан',
    },
    storefront_conversations: {
      running: 'Чета разговори с клиенти',
      done: 'Прочетени разговори с клиенти',
      failed: 'Разговорите с клиенти не можаха да бъдат прочетени',
    },
    read_knowledge_page: {
      running: 'Чета страница със знания',
      done: 'Прочетена страница със знания',
      failed: 'Страницата със знания не можа да бъде прочетена',
      counted:
        'one:Прочетена {count} страница със знания|other:Прочетени {count} страници със знания',
    },
    search_work_results: {
      running: 'Търся в запазените резултати',
      done: 'Претърсени запазени резултати',
      failed: 'Запазените резултати не можаха да бъдат претърсени',
    },
    read_work_result: {
      running: 'Чета запазен резултат',
      done: 'Прочетен запазен резултат',
      failed: 'Запазеният резултат не можа да бъде прочетен',
      counted: 'one:Прочетен {count} запазен резултат|other:Прочетени {count} запазени резултата',
    },
    read_previous_chat: {
      running: 'Чета предишен чат',
      done: 'Прочетен предишен чат',
      failed: 'Предишният чат не можа да бъде прочетен',
      counted: 'one:Прочетен {count} предишен чат|other:Прочетени {count} предишни чата',
    },
    read_connected_document: {
      running: 'Чета свързан документ',
      done: 'Прочетен свързан документ',
      failed: 'Свързаният документ не можа да бъде прочетен',
      counted: 'one:Прочетен {count} свързан документ|other:Прочетени {count} свързани документа',
    },
    read_url: {
      running: 'Чета уеб страница',
      done: 'Прочетена уеб страница',
      failed: 'Уеб страницата не можа да бъде прочетена',
      counted: 'one:Прочетена {count} уеб страница|other:Прочетени {count} уеб страници',
    },
    open_web: {
      running: 'Чета уеб страница',
      done: 'Прочетена уеб страница',
      failed: 'Уеб страницата не можа да бъде прочетена',
      counted: 'one:Прочетена {count} уеб страница|other:Прочетени {count} уеб страници',
    },
    view_image: {
      running: 'Разглеждам изображение',
      done: 'Разгледано изображение',
      failed: 'Изображението не можа да бъде разгледано',
      counted: 'one:Разгледано {count} изображение|other:Разгледани {count} изображения',
    },
    read_company_skill: {
      running: 'Проверявам Skills',
      done: 'Проверени Skills',
      failed: 'Skills не можаха да бъдат проверени',
    },
    find_right_skill: {
      running: 'Търся подходящите Skills',
      done: 'Намерени Skills',
      failed: 'Skills не можаха да бъдат намерени',
    },
    list_company_skills: {
      running: 'Проверявам Skills',
      done: 'Проверени Skills',
      failed: 'Skills не можаха да бъдат проверени',
    },
    read_my_settings: {
      running: 'Проверявам настройките ви',
      done: 'Настройките ви са проверени',
      failed: 'Настройките ви не можаха да бъдат проверени',
    },
    read_account_overview: {
      running: 'Проверявам работното ви пространство',
      done: 'Работното ви пространство е проверено',
      failed: 'Работното ви пространство не можа да бъде проверено',
    },
    list_team: {
      running: 'Проверявам екипа',
      done: 'Екипът е проверен',
      failed: 'Екипът не можа да бъде проверен',
    },
    read_company_usage: {
      running: 'Проверявам използването',
      done: 'Използването е проверено',
      failed: 'Използването не можа да бъде проверено',
    },
    list_connection_tools: {
      running: 'Проверявам свързаните приложения',
      done: 'Свързаните приложения са проверени',
      failed: 'Свързаните приложения не можаха да бъдат проверени',
    },
    read_connection_action: {
      running: 'Проверявам свързаните приложения',
      done: 'Свързаните приложения са проверени',
      failed: 'Свързаните приложения не можаха да бъдат проверени',
    },
    list_custom_mcp_tools: {
      running: 'Проверявам свързаните приложения',
      done: 'Свързаните приложения са проверени',
      failed: 'Свързаните приложения не можаха да бъдат проверени',
    },
    read_custom_mcp_action: {
      running: 'Проверявам свързаните приложения',
      done: 'Свързаните приложения са проверени',
      failed: 'Свързаните приложения не можаха да бъдат проверени',
    },
  },
  cs: {
    // Projects v1.
    remove_from_project: {
      running: 'Odebírám z projektu',
      done: 'Odebráno z projektu',
      failed: 'Z projektu se nepodařilo odebrat',
    },
    project_context: {
      running: 'Čtu zadání projektu',
      done: 'Zadání projektu přečteno',
      failed: 'Zadání projektu se nepodařilo přečíst',
    },
    read_projects: {
      running: 'Kontroluji projekty',
      done: 'Projekty zkontrolovány',
      failed: 'Projekty se nepodařilo zkontrolovat',
    },
    draft_project: {
      running: 'Připravuji návrh projektu',
      done: 'Návrh projektu připraven',
      failed: 'Návrh projektu se nepodařilo připravit',
    },
    update_project: {
      running: 'Připravuji změnu projektu',
      done: 'Změna projektu připravena',
      failed: 'Změnu projektu se nepodařilo připravit',
    },
    move_chat_to_project: {
      running: 'Přesouvám chat',
      done: 'Chat přesunut',
      failed: 'Chat se nepodařilo přesunout',
    },
    add_to_project: {
      running: 'Přidávám do projektu',
      done: 'Přidáno do projektu',
      failed: 'Do projektu se nepodařilo přidat',
    },
    suggest_project_move: {
      running: 'Zjišťuji, kam chat patří',
      done: 'Navržen projekt',
      failed: 'Projekt se nepodařilo navrhnout',
    },
    search_company_knowledge: {
      running: 'Prohledávám znalosti firmy',
      done: 'Znalosti firmy prohledány',
      failed: 'Znalosti firmy se nepodařilo prohledat',
    },
    read_company_context: {
      running: 'Čtu kontext firmy',
      done: 'Kontext firmy přečten',
      failed: 'Kontext firmy se nepodařilo přečíst',
    },
    find_company_work: {
      running: 'Procházím práci firmy',
      done: 'Práce firmy prohlédnuta',
      failed: 'Práci firmy se nepodařilo projít',
    },
    read_company_meeting: {
      running: 'Čtu schůzku',
      done: 'Schůzka přečtena',
      failed: 'Schůzku se nepodařilo přečíst',
      counted:
        'one:Přečtena {count} schůzka|few:Přečteny {count} schůzky|many:Přečteno {count} schůzky|other:Přečteno {count} schůzek',
    },
    read_company_file: {
      running: 'Čtu soubor',
      done: 'Soubor přečten',
      failed: 'Soubor se nepodařilo přečíst',
      counted:
        'one:Přečten {count} soubor|few:Přečteny {count} soubory|many:Přečteno {count} souboru|other:Přečteno {count} souborů',
    },
    search_company_file: {
      running: 'Prohledávám soubor',
      done: 'Soubor prohledán',
      failed: 'Soubor se nepodařilo prohledat',
      counted:
        'one:Prohledán {count} soubor|few:Prohledány {count} soubory|many:Prohledáno {count} souboru|other:Prohledáno {count} souborů',
    },
    read_company_source: {
      running: 'Čtu dokument',
      done: 'Dokument přečten',
      failed: 'Dokument se nepodařilo přečíst',
      counted:
        'one:Přečten {count} dokument|few:Přečteny {count} dokumenty|many:Přečteno {count} dokumentu|other:Přečteno {count} dokumentů',
    },
    storefront_stores: {
      running: 'Kontroluji nákupní asistenty',
      done: 'Nákupní asistenti zkontrolováni',
      failed: 'Nákupní asistenty se nepodařilo zkontrolovat',
    },
    render_storefront_preview: {
      running: 'Prohlížím nákupního asistenta',
      done: 'Nákupní asistent prohlédnut',
      failed: 'Nákupního asistenta se nepodařilo prohlédnout',
    },
    storefront_conversations: {
      running: 'Čtu konverzace se zákazníky',
      done: 'Konverzace se zákazníky přečteny',
      failed: 'Konverzace se zákazníky se nepodařilo přečíst',
    },
    read_knowledge_page: {
      running: 'Čtu stránku znalostí',
      done: 'Stránka znalostí přečtena',
      failed: 'Stránku znalostí se nepodařilo přečíst',
      counted:
        'one:Přečtena {count} stránka znalostí|few:Přečteny {count} stránky znalostí|many:Přečteno {count} stránky znalostí|other:Přečteno {count} stránek znalostí',
    },
    search_work_results: {
      running: 'Prohledávám uložené výsledky',
      done: 'Uložené výsledky prohledány',
      failed: 'Uložené výsledky se nepodařilo prohledat',
    },
    read_work_result: {
      running: 'Čtu uložený výsledek',
      done: 'Uložený výsledek přečten',
      failed: 'Uložený výsledek se nepodařilo přečíst',
      counted:
        'one:Přečten {count} uložený výsledek|few:Přečteny {count} uložené výsledky|many:Přečteno {count} uloženého výsledku|other:Přečteno {count} uložených výsledků',
    },
    read_previous_chat: {
      running: 'Čtu dřívější chat',
      done: 'Dřívější chat přečten',
      failed: 'Dřívější chat se nepodařilo přečíst',
      counted:
        'one:Přečten {count} dřívější chat|few:Přečteny {count} dřívější chaty|many:Přečteno {count} dřívějšího chatu|other:Přečteno {count} dřívějších chatů',
    },
    read_connected_document: {
      running: 'Čtu připojený dokument',
      done: 'Připojený dokument přečten',
      failed: 'Připojený dokument se nepodařilo přečíst',
      counted:
        'one:Přečten {count} připojený dokument|few:Přečteny {count} připojené dokumenty|many:Přečteno {count} připojeného dokumentu|other:Přečteno {count} připojených dokumentů',
    },
    read_url: {
      running: 'Čtu webovou stránku',
      done: 'Webová stránka přečtena',
      failed: 'Webovou stránku se nepodařilo přečíst',
      counted:
        'one:Přečtena {count} webová stránka|few:Přečteny {count} webové stránky|many:Přečteno {count} webové stránky|other:Přečteno {count} webových stránek',
    },
    open_web: {
      running: 'Čtu webovou stránku',
      done: 'Webová stránka přečtena',
      failed: 'Webovou stránku se nepodařilo přečíst',
      counted:
        'one:Přečtena {count} webová stránka|few:Přečteny {count} webové stránky|many:Přečteno {count} webové stránky|other:Přečteno {count} webových stránek',
    },
    view_image: {
      running: 'Prohlížím obrázek',
      done: 'Obrázek prohlédnut',
      failed: 'Obrázek se nepodařilo prohlédnout',
      counted:
        'one:Prohlédnut {count} obrázek|few:Prohlédnuty {count} obrázky|many:Prohlédnuto {count} obrázku|other:Prohlédnuto {count} obrázků',
    },
    read_company_skill: {
      running: 'Kontroluji Skills',
      done: 'Skills zkontrolovány',
      failed: 'Skills se nepodařilo zkontrolovat',
    },
    find_right_skill: {
      running: 'Hledám vhodné Skills',
      done: 'Skills nalezeny',
      failed: 'Skills se nepodařilo najít',
    },
    list_company_skills: {
      running: 'Kontroluji Skills',
      done: 'Skills zkontrolovány',
      failed: 'Skills se nepodařilo zkontrolovat',
    },
    read_my_settings: {
      running: 'Kontroluji vaše nastavení',
      done: 'Vaše nastavení zkontrolováno',
      failed: 'Vaše nastavení se nepodařilo zkontrolovat',
    },
    read_account_overview: {
      running: 'Kontroluji váš pracovní prostor',
      done: 'Váš pracovní prostor zkontrolován',
      failed: 'Váš pracovní prostor se nepodařilo zkontrolovat',
    },
    list_team: {
      running: 'Kontroluji tým',
      done: 'Tým zkontrolován',
      failed: 'Tým se nepodařilo zkontrolovat',
    },
    read_company_usage: {
      running: 'Kontroluji využití',
      done: 'Využití zkontrolováno',
      failed: 'Využití se nepodařilo zkontrolovat',
    },
    list_connection_tools: {
      running: 'Kontroluji připojené aplikace',
      done: 'Připojené aplikace zkontrolovány',
      failed: 'Připojené aplikace se nepodařilo zkontrolovat',
    },
    read_connection_action: {
      running: 'Kontroluji připojené aplikace',
      done: 'Připojené aplikace zkontrolovány',
      failed: 'Připojené aplikace se nepodařilo zkontrolovat',
    },
    list_custom_mcp_tools: {
      running: 'Kontroluji připojené aplikace',
      done: 'Připojené aplikace zkontrolovány',
      failed: 'Připojené aplikace se nepodařilo zkontrolovat',
    },
    read_custom_mcp_action: {
      running: 'Kontroluji připojené aplikace',
      done: 'Připojené aplikace zkontrolovány',
      failed: 'Připojené aplikace se nepodařilo zkontrolovat',
    },
  },
  da: {
    // Projects v1.
    remove_from_project: {
      running: 'Tager det ud af projektet',
      done: 'Tog det ud af projektet',
      failed: 'Kunne ikke tage det ud af projektet',
    },
    project_context: {
      running: 'Læser projektets brief',
      done: 'Læste projektets brief',
      failed: 'Kunne ikke læse projektets brief',
    },
    read_projects: {
      running: 'Tjekker projekter',
      done: 'Tjekkede projekter',
      failed: 'Kunne ikke tjekke projekter',
    },
    draft_project: {
      running: 'Laver et udkast til projektet',
      done: 'Lavede et udkast til projektet',
      failed: 'Kunne ikke lave et udkast til projektet',
    },
    update_project: {
      running: 'Forbereder ændringen af projektet',
      done: 'Forberedte ændringen af projektet',
      failed: 'Kunne ikke forberede ændringen af projektet',
    },
    move_chat_to_project: {
      running: 'Flytter chatten',
      done: 'Flyttede chatten',
      failed: 'Kunne ikke flytte chatten',
    },
    add_to_project: {
      running: 'Tilføjer til projektet',
      done: 'Tilføjede til projektet',
      failed: 'Kunne ikke tilføje til projektet',
    },
    suggest_project_move: {
      running: 'Tjekker, hvor chatten hører til',
      done: 'Foreslog et projekt',
      failed: 'Kunne ikke foreslå et projekt',
    },
    search_company_knowledge: {
      running: 'Søger i virksomhedens viden',
      done: 'Søgte i virksomhedens viden',
      failed: 'Kunne ikke søge i virksomhedens viden',
    },
    read_company_context: {
      running: 'Læser virksomhedens kontekst',
      done: 'Læste virksomhedens kontekst',
      failed: 'Kunne ikke læse virksomhedens kontekst',
    },
    find_company_work: {
      running: 'Gennemgår virksomhedens arbejde',
      done: 'Gennemgik virksomhedens arbejde',
      failed: 'Kunne ikke gennemgå virksomhedens arbejde',
    },
    read_company_meeting: {
      running: 'Læser et møde',
      done: 'Læste et møde',
      failed: 'Kunne ikke læse et møde',
      counted: 'one:Læste {count} møde|other:Læste {count} møder',
    },
    read_company_file: {
      running: 'Læser en fil',
      done: 'Læste en fil',
      failed: 'Kunne ikke læse en fil',
      counted: 'one:Læste {count} fil|other:Læste {count} filer',
    },
    search_company_file: {
      running: 'Søger i en fil',
      done: 'Søgte i en fil',
      failed: 'Kunne ikke søge i en fil',
      counted: 'one:Søgte i {count} fil|other:Søgte i {count} filer',
    },
    read_company_source: {
      running: 'Læser et dokument',
      done: 'Læste et dokument',
      failed: 'Kunne ikke læse et dokument',
      counted: 'one:Læste {count} dokument|other:Læste {count} dokumenter',
    },
    storefront_stores: {
      running: 'Tjekker jeres shoppingassistenter',
      done: 'Tjekkede jeres shoppingassistenter',
      failed: 'Kunne ikke tjekke jeres shoppingassistenter',
    },
    render_storefront_preview: {
      running: 'Ser på shoppingassistenten',
      done: 'Så på shoppingassistenten',
      failed: 'Kunne ikke se shoppingassistenten',
    },
    storefront_conversations: {
      running: 'Læser samtaler med kunder',
      done: 'Læste samtaler med kunder',
      failed: 'Kunne ikke læse samtaler med kunder',
    },
    read_knowledge_page: {
      running: 'Læser en vidensside',
      done: 'Læste en vidensside',
      failed: 'Kunne ikke læse en vidensside',
      counted: 'one:Læste {count} vidensside|other:Læste {count} videnssider',
    },
    search_work_results: {
      running: 'Søger i gemte resultater',
      done: 'Søgte i gemte resultater',
      failed: 'Kunne ikke søge i gemte resultater',
    },
    read_work_result: {
      running: 'Læser et gemt resultat',
      done: 'Læste et gemt resultat',
      failed: 'Kunne ikke læse et gemt resultat',
      counted: 'one:Læste {count} gemt resultat|other:Læste {count} gemte resultater',
    },
    read_previous_chat: {
      running: 'Læser en tidligere chat',
      done: 'Læste en tidligere chat',
      failed: 'Kunne ikke læse en tidligere chat',
      counted: 'one:Læste {count} tidligere chat|other:Læste {count} tidligere chats',
    },
    read_connected_document: {
      running: 'Læser et forbundet dokument',
      done: 'Læste et forbundet dokument',
      failed: 'Kunne ikke læse et forbundet dokument',
      counted: 'one:Læste {count} forbundet dokument|other:Læste {count} forbundne dokumenter',
    },
    read_url: {
      running: 'Læser en webside',
      done: 'Læste en webside',
      failed: 'Kunne ikke læse en webside',
      counted: 'one:Læste {count} webside|other:Læste {count} websider',
    },
    open_web: {
      running: 'Læser en webside',
      done: 'Læste en webside',
      failed: 'Kunne ikke læse en webside',
      counted: 'one:Læste {count} webside|other:Læste {count} websider',
    },
    view_image: {
      running: 'Ser på et billede',
      done: 'Så på et billede',
      failed: 'Kunne ikke se på et billede',
      counted: 'one:Så på {count} billede|other:Så på {count} billeder',
    },
    read_company_skill: {
      running: 'Tjekker Skills',
      done: 'Tjekkede Skills',
      failed: 'Kunne ikke tjekke Skills',
    },
    find_right_skill: {
      running: 'Finder de rigtige Skills',
      done: 'Fandt Skills',
      failed: 'Kunne ikke finde Skills',
    },
    list_company_skills: {
      running: 'Tjekker Skills',
      done: 'Tjekkede Skills',
      failed: 'Kunne ikke tjekke Skills',
    },
    read_my_settings: {
      running: 'Tjekker dine indstillinger',
      done: 'Tjekkede dine indstillinger',
      failed: 'Kunne ikke tjekke dine indstillinger',
    },
    read_account_overview: {
      running: 'Tjekker dit arbejdsområde',
      done: 'Tjekkede dit arbejdsområde',
      failed: 'Kunne ikke tjekke dit arbejdsområde',
    },
    list_team: {
      running: 'Tjekker teamet',
      done: 'Tjekkede teamet',
      failed: 'Kunne ikke tjekke teamet',
    },
    read_company_usage: {
      running: 'Tjekker forbruget',
      done: 'Tjekkede forbruget',
      failed: 'Kunne ikke tjekke forbruget',
    },
    list_connection_tools: {
      running: 'Tjekker forbundne apps',
      done: 'Tjekkede forbundne apps',
      failed: 'Kunne ikke tjekke forbundne apps',
    },
    read_connection_action: {
      running: 'Tjekker forbundne apps',
      done: 'Tjekkede forbundne apps',
      failed: 'Kunne ikke tjekke forbundne apps',
    },
    list_custom_mcp_tools: {
      running: 'Tjekker forbundne apps',
      done: 'Tjekkede forbundne apps',
      failed: 'Kunne ikke tjekke forbundne apps',
    },
    read_custom_mcp_action: {
      running: 'Tjekker forbundne apps',
      done: 'Tjekkede forbundne apps',
      failed: 'Kunne ikke tjekke forbundne apps',
    },
  },
  de: {
    // Projects v1.
    remove_from_project: {
      running: 'Nehme es aus dem Projekt',
      done: 'Aus dem Projekt genommen',
      failed: 'Konnte nicht aus dem Projekt genommen werden',
    },
    project_context: {
      running: 'Lese das Projekt-Briefing',
      done: 'Projekt-Briefing gelesen',
      failed: 'Projekt-Briefing konnte nicht gelesen werden',
    },
    read_projects: {
      running: 'Prüfe Projekte',
      done: 'Projekte geprüft',
      failed: 'Projekte konnten nicht geprüft werden',
    },
    draft_project: {
      running: 'Entwerfe das Projekt',
      done: 'Projekt entworfen',
      failed: 'Projekt konnte nicht entworfen werden',
    },
    update_project: {
      running: 'Bereite die Projektänderung vor',
      done: 'Projektänderung vorbereitet',
      failed: 'Projektänderung konnte nicht vorbereitet werden',
    },
    move_chat_to_project: {
      running: 'Verschiebe den Chat',
      done: 'Chat verschoben',
      failed: 'Chat konnte nicht verschoben werden',
    },
    add_to_project: {
      running: 'Füge zum Projekt hinzu',
      done: 'Zum Projekt hinzugefügt',
      failed: 'Konnte nicht zum Projekt hinzugefügt werden',
    },
    suggest_project_move: {
      running: 'Prüfe, wohin der Chat gehört',
      done: 'Projekt vorgeschlagen',
      failed: 'Konnte kein Projekt vorschlagen',
    },
    search_company_knowledge: {
      running: 'Durchsuche Unternehmenswissen',
      done: 'Unternehmenswissen durchsucht',
      failed: 'Unternehmenswissen konnte nicht durchsucht werden',
    },
    read_company_context: {
      running: 'Lese Unternehmenskontext',
      done: 'Unternehmenskontext gelesen',
      failed: 'Unternehmenskontext konnte nicht gelesen werden',
    },
    find_company_work: {
      running: 'Sehe Arbeiten des Unternehmens durch',
      done: 'Arbeiten des Unternehmens durchgesehen',
      failed: 'Arbeiten des Unternehmens konnten nicht durchgesehen werden',
    },
    read_company_meeting: {
      running: 'Lese eine Besprechung',
      done: 'Besprechung gelesen',
      failed: 'Besprechung konnte nicht gelesen werden',
      counted: 'one:{count} Besprechung gelesen|other:{count} Besprechungen gelesen',
    },
    read_company_file: {
      running: 'Lese eine Datei',
      done: 'Datei gelesen',
      failed: 'Datei konnte nicht gelesen werden',
      counted: 'one:{count} Datei gelesen|other:{count} Dateien gelesen',
    },
    search_company_file: {
      running: 'Durchsuche eine Datei',
      done: 'Datei durchsucht',
      failed: 'Datei konnte nicht durchsucht werden',
      counted: 'one:{count} Datei durchsucht|other:{count} Dateien durchsucht',
    },
    read_company_source: {
      running: 'Lese ein Dokument',
      done: 'Dokument gelesen',
      failed: 'Dokument konnte nicht gelesen werden',
      counted: 'one:{count} Dokument gelesen|other:{count} Dokumente gelesen',
    },
    storefront_stores: {
      running: 'Prüfe die Shopping-Assistenten',
      done: 'Shopping-Assistenten geprüft',
      failed: 'Shopping-Assistenten konnten nicht geprüft werden',
    },
    render_storefront_preview: {
      running: 'Sehe mir den Shopping-Assistenten an',
      done: 'Shopping-Assistenten angesehen',
      failed: 'Shopping-Assistent konnte nicht angezeigt werden',
    },
    storefront_conversations: {
      running: 'Lese Kundengespräche',
      done: 'Kundengespräche gelesen',
      failed: 'Kundengespräche konnten nicht gelesen werden',
    },
    read_knowledge_page: {
      running: 'Lese eine Wissensseite',
      done: 'Wissensseite gelesen',
      failed: 'Wissensseite konnte nicht gelesen werden',
      counted: 'one:{count} Wissensseite gelesen|other:{count} Wissensseiten gelesen',
    },
    search_work_results: {
      running: 'Durchsuche gespeicherte Ergebnisse',
      done: 'Gespeicherte Ergebnisse durchsucht',
      failed: 'Gespeicherte Ergebnisse konnten nicht durchsucht werden',
    },
    read_work_result: {
      running: 'Lese ein gespeichertes Ergebnis',
      done: 'Gespeichertes Ergebnis gelesen',
      failed: 'Gespeichertes Ergebnis konnte nicht gelesen werden',
      counted:
        'one:{count} gespeichertes Ergebnis gelesen|other:{count} gespeicherte Ergebnisse gelesen',
    },
    read_previous_chat: {
      running: 'Lese einen früheren Chat',
      done: 'Früheren Chat gelesen',
      failed: 'Früherer Chat konnte nicht gelesen werden',
      counted: 'one:{count} früheren Chat gelesen|other:{count} frühere Chats gelesen',
    },
    read_connected_document: {
      running: 'Lese ein verbundenes Dokument',
      done: 'Verbundenes Dokument gelesen',
      failed: 'Verbundenes Dokument konnte nicht gelesen werden',
      counted:
        'one:{count} verbundenes Dokument gelesen|other:{count} verbundene Dokumente gelesen',
    },
    read_url: {
      running: 'Lese eine Webseite',
      done: 'Webseite gelesen',
      failed: 'Webseite konnte nicht gelesen werden',
      counted: 'one:{count} Webseite gelesen|other:{count} Webseiten gelesen',
    },
    open_web: {
      running: 'Lese eine Webseite',
      done: 'Webseite gelesen',
      failed: 'Webseite konnte nicht gelesen werden',
      counted: 'one:{count} Webseite gelesen|other:{count} Webseiten gelesen',
    },
    view_image: {
      running: 'Sehe mir ein Bild an',
      done: 'Bild angesehen',
      failed: 'Bild konnte nicht angesehen werden',
      counted: 'one:{count} Bild angesehen|other:{count} Bilder angesehen',
    },
    read_company_skill: {
      running: 'Prüfe Skills',
      done: 'Skills geprüft',
      failed: 'Skills konnten nicht geprüft werden',
    },
    find_right_skill: {
      running: 'Suche passende Skills',
      done: 'Passende Skills gefunden',
      failed: 'Skills konnten nicht gefunden werden',
    },
    list_company_skills: {
      running: 'Prüfe Skills',
      done: 'Skills geprüft',
      failed: 'Skills konnten nicht geprüft werden',
    },
    read_my_settings: {
      running: 'Prüfe Ihre Einstellungen',
      done: 'Ihre Einstellungen geprüft',
      failed: 'Ihre Einstellungen konnten nicht geprüft werden',
    },
    read_account_overview: {
      running: 'Prüfe Ihren Arbeitsbereich',
      done: 'Ihren Arbeitsbereich geprüft',
      failed: 'Ihr Arbeitsbereich konnte nicht geprüft werden',
    },
    list_team: {
      running: 'Prüfe das Team',
      done: 'Team geprüft',
      failed: 'Team konnte nicht geprüft werden',
    },
    read_company_usage: {
      running: 'Prüfe die Nutzung',
      done: 'Nutzung geprüft',
      failed: 'Nutzung konnte nicht geprüft werden',
    },
    list_connection_tools: {
      running: 'Prüfe verbundene Apps',
      done: 'Verbundene Apps geprüft',
      failed: 'Verbundene Apps konnten nicht geprüft werden',
    },
    read_connection_action: {
      running: 'Prüfe verbundene Apps',
      done: 'Verbundene Apps geprüft',
      failed: 'Verbundene Apps konnten nicht geprüft werden',
    },
    list_custom_mcp_tools: {
      running: 'Prüfe verbundene Apps',
      done: 'Verbundene Apps geprüft',
      failed: 'Verbundene Apps konnten nicht geprüft werden',
    },
    read_custom_mcp_action: {
      running: 'Prüfe verbundene Apps',
      done: 'Verbundene Apps geprüft',
      failed: 'Verbundene Apps konnten nicht geprüft werden',
    },
  },
  el: {
    // Projects v1.
    remove_from_project: {
      running: 'Αφαίρεση από το έργο',
      done: 'Αφαιρέθηκε από το έργο',
      failed: 'Δεν ήταν δυνατή η αφαίρεση από το έργο',
    },
    project_context: {
      running: 'Ανάγνωση της περιγραφής του έργου',
      done: 'Διαβάστηκε η περιγραφή του έργου',
      failed: 'Δεν ήταν δυνατή η ανάγνωση της περιγραφής του έργου',
    },
    read_projects: {
      running: 'Έλεγχος έργων',
      done: 'Ελέγχθηκαν τα έργα',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των έργων',
    },
    draft_project: {
      running: 'Σύνταξη προσχεδίου του έργου',
      done: 'Συντάχθηκε προσχέδιο του έργου',
      failed: 'Δεν ήταν δυνατή η σύνταξη προσχεδίου του έργου',
    },
    update_project: {
      running: 'Προετοιμασία της αλλαγής του έργου',
      done: 'Προετοιμάστηκε η αλλαγή του έργου',
      failed: 'Δεν ήταν δυνατή η προετοιμασία της αλλαγής του έργου',
    },
    move_chat_to_project: {
      running: 'Μετακίνηση της συνομιλίας',
      done: 'Η συνομιλία μετακινήθηκε',
      failed: 'Δεν ήταν δυνατή η μετακίνηση της συνομιλίας',
    },
    add_to_project: {
      running: 'Προσθήκη στο έργο',
      done: 'Προστέθηκε στο έργο',
      failed: 'Δεν ήταν δυνατή η προσθήκη στο έργο',
    },
    suggest_project_move: {
      running: 'Έλεγχος σε ποιο έργο ανήκει η συνομιλία',
      done: 'Προτάθηκε έργο',
      failed: 'Δεν ήταν δυνατή η πρόταση έργου',
    },
    search_company_knowledge: {
      running: 'Αναζήτηση στη γνώση της εταιρείας',
      done: 'Έγινε αναζήτηση στη γνώση της εταιρείας',
      failed: 'Δεν ήταν δυνατή η αναζήτηση στη γνώση της εταιρείας',
    },
    read_company_context: {
      running: 'Ανάγνωση του πλαισίου της εταιρείας',
      done: 'Έγινε ανάγνωση του πλαισίου της εταιρείας',
      failed: 'Δεν ήταν δυνατή η ανάγνωση του πλαισίου της εταιρείας',
    },
    find_company_work: {
      running: 'Εξέταση της εργασίας της εταιρείας',
      done: 'Έγινε εξέταση της εργασίας της εταιρείας',
      failed: 'Δεν ήταν δυνατή η εξέταση της εργασίας της εταιρείας',
    },
    read_company_meeting: {
      running: 'Ανάγνωση σύσκεψης',
      done: 'Έγινε ανάγνωση σύσκεψης',
      failed: 'Δεν ήταν δυνατή η ανάγνωση σύσκεψης',
      counted: 'one:Έγινε ανάγνωση {count} σύσκεψης|other:Έγινε ανάγνωση {count} συσκέψεων',
    },
    read_company_file: {
      running: 'Ανάγνωση αρχείου',
      done: 'Έγινε ανάγνωση αρχείου',
      failed: 'Δεν ήταν δυνατή η ανάγνωση αρχείου',
      counted: 'one:Έγινε ανάγνωση {count} αρχείου|other:Έγινε ανάγνωση {count} αρχείων',
    },
    search_company_file: {
      running: 'Αναζήτηση σε αρχείο',
      done: 'Έγινε αναζήτηση σε αρχείο',
      failed: 'Δεν ήταν δυνατή η αναζήτηση σε αρχείο',
      counted: 'one:Έγινε αναζήτηση σε {count} αρχείο|other:Έγινε αναζήτηση σε {count} αρχεία',
    },
    read_company_source: {
      running: 'Ανάγνωση εγγράφου',
      done: 'Έγινε ανάγνωση εγγράφου',
      failed: 'Δεν ήταν δυνατή η ανάγνωση εγγράφου',
      counted: 'one:Έγινε ανάγνωση {count} εγγράφου|other:Έγινε ανάγνωση {count} εγγράφων',
    },
    storefront_stores: {
      running: 'Έλεγχος βοηθών αγορών',
      done: 'Έγινε έλεγχος βοηθών αγορών',
      failed: 'Δεν ήταν δυνατός ο έλεγχος βοηθών αγορών',
    },
    render_storefront_preview: {
      running: 'Προβολή του βοηθού αγορών',
      done: 'Έγινε προβολή του βοηθού αγορών',
      failed: 'Δεν ήταν δυνατή η προβολή του βοηθού αγορών',
    },
    storefront_conversations: {
      running: 'Ανάγνωση συνομιλιών πελατών',
      done: 'Έγινε ανάγνωση συνομιλιών πελατών',
      failed: 'Δεν ήταν δυνατή η ανάγνωση συνομιλιών πελατών',
    },
    read_knowledge_page: {
      running: 'Ανάγνωση σελίδας γνώσης',
      done: 'Έγινε ανάγνωση σελίδας γνώσης',
      failed: 'Δεν ήταν δυνατή η ανάγνωση σελίδας γνώσης',
      counted:
        'one:Έγινε ανάγνωση {count} σελίδας γνώσης|other:Έγινε ανάγνωση {count} σελίδων γνώσης',
    },
    search_work_results: {
      running: 'Αναζήτηση στα αποθηκευμένα αποτελέσματα',
      done: 'Έγινε αναζήτηση στα αποθηκευμένα αποτελέσματα',
      failed: 'Δεν ήταν δυνατή η αναζήτηση στα αποθηκευμένα αποτελέσματα',
    },
    read_work_result: {
      running: 'Ανάγνωση αποθηκευμένου αποτελέσματος',
      done: 'Έγινε ανάγνωση αποθηκευμένου αποτελέσματος',
      failed: 'Δεν ήταν δυνατή η ανάγνωση αποθηκευμένου αποτελέσματος',
      counted:
        'one:Έγινε ανάγνωση {count} αποθηκευμένου αποτελέσματος|other:Έγινε ανάγνωση {count} αποθηκευμένων αποτελεσμάτων',
    },
    read_previous_chat: {
      running: 'Ανάγνωση προηγούμενης συνομιλίας',
      done: 'Έγινε ανάγνωση προηγούμενης συνομιλίας',
      failed: 'Δεν ήταν δυνατή η ανάγνωση προηγούμενης συνομιλίας',
      counted:
        'one:Έγινε ανάγνωση {count} προηγούμενης συνομιλίας|other:Έγινε ανάγνωση {count} προηγούμενων συνομιλιών',
    },
    read_connected_document: {
      running: 'Ανάγνωση συνδεδεμένου εγγράφου',
      done: 'Έγινε ανάγνωση συνδεδεμένου εγγράφου',
      failed: 'Δεν ήταν δυνατή η ανάγνωση συνδεδεμένου εγγράφου',
      counted:
        'one:Έγινε ανάγνωση {count} συνδεδεμένου εγγράφου|other:Έγινε ανάγνωση {count} συνδεδεμένων εγγράφων',
    },
    read_url: {
      running: 'Ανάγνωση ιστοσελίδας',
      done: 'Έγινε ανάγνωση ιστοσελίδας',
      failed: 'Δεν ήταν δυνατή η ανάγνωση ιστοσελίδας',
      counted: 'one:Έγινε ανάγνωση {count} ιστοσελίδας|other:Έγινε ανάγνωση {count} ιστοσελίδων',
    },
    open_web: {
      running: 'Ανάγνωση ιστοσελίδας',
      done: 'Έγινε ανάγνωση ιστοσελίδας',
      failed: 'Δεν ήταν δυνατή η ανάγνωση ιστοσελίδας',
      counted: 'one:Έγινε ανάγνωση {count} ιστοσελίδας|other:Έγινε ανάγνωση {count} ιστοσελίδων',
    },
    view_image: {
      running: 'Προβολή εικόνας',
      done: 'Έγινε προβολή εικόνας',
      failed: 'Δεν ήταν δυνατή η προβολή εικόνας',
      counted: 'one:Έγινε προβολή {count} εικόνας|other:Έγινε προβολή {count} εικόνων',
    },
    read_company_skill: {
      running: 'Έλεγχος των Skills',
      done: 'Έγινε έλεγχος των Skills',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των Skills',
    },
    find_right_skill: {
      running: 'Αναζήτηση των κατάλληλων Skills',
      done: 'Βρέθηκαν Skills',
      failed: 'Δεν ήταν δυνατή η εύρεση Skills',
    },
    list_company_skills: {
      running: 'Έλεγχος των Skills',
      done: 'Έγινε έλεγχος των Skills',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των Skills',
    },
    read_my_settings: {
      running: 'Έλεγχος των ρυθμίσεών σας',
      done: 'Έγινε έλεγχος των ρυθμίσεών σας',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των ρυθμίσεών σας',
    },
    read_account_overview: {
      running: 'Έλεγχος του χώρου εργασίας σας',
      done: 'Έγινε έλεγχος του χώρου εργασίας σας',
      failed: 'Δεν ήταν δυνατός ο έλεγχος του χώρου εργασίας σας',
    },
    list_team: {
      running: 'Έλεγχος της ομάδας',
      done: 'Έγινε έλεγχος της ομάδας',
      failed: 'Δεν ήταν δυνατός ο έλεγχος της ομάδας',
    },
    read_company_usage: {
      running: 'Έλεγχος της χρήσης',
      done: 'Έγινε έλεγχος της χρήσης',
      failed: 'Δεν ήταν δυνατός ο έλεγχος της χρήσης',
    },
    list_connection_tools: {
      running: 'Έλεγχος των συνδεδεμένων εφαρμογών',
      done: 'Έγινε έλεγχος των συνδεδεμένων εφαρμογών',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των συνδεδεμένων εφαρμογών',
    },
    read_connection_action: {
      running: 'Έλεγχος των συνδεδεμένων εφαρμογών',
      done: 'Έγινε έλεγχος των συνδεδεμένων εφαρμογών',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των συνδεδεμένων εφαρμογών',
    },
    list_custom_mcp_tools: {
      running: 'Έλεγχος των συνδεδεμένων εφαρμογών',
      done: 'Έγινε έλεγχος των συνδεδεμένων εφαρμογών',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των συνδεδεμένων εφαρμογών',
    },
    read_custom_mcp_action: {
      running: 'Έλεγχος των συνδεδεμένων εφαρμογών',
      done: 'Έγινε έλεγχος των συνδεδεμένων εφαρμογών',
      failed: 'Δεν ήταν δυνατός ο έλεγχος των συνδεδεμένων εφαρμογών',
    },
  },
  es: {
    // Projects v1.
    remove_from_project: {
      running: 'Quitando del proyecto',
      done: 'Quitado del proyecto',
      failed: 'No se pudo quitar del proyecto',
    },
    project_context: {
      running: 'Leyendo el brief del proyecto',
      done: 'Brief del proyecto leído',
      failed: 'No se pudo leer el brief del proyecto',
    },
    read_projects: {
      running: 'Revisando proyectos',
      done: 'Proyectos revisados',
      failed: 'No se pudieron revisar los proyectos',
    },
    draft_project: {
      running: 'Preparando el borrador del proyecto',
      done: 'Borrador del proyecto preparado',
      failed: 'No se pudo preparar el borrador del proyecto',
    },
    update_project: {
      running: 'Preparando el cambio del proyecto',
      done: 'Cambio del proyecto preparado',
      failed: 'No se pudo preparar el cambio del proyecto',
    },
    move_chat_to_project: {
      running: 'Moviendo el chat',
      done: 'Chat movido',
      failed: 'No se pudo mover el chat',
    },
    add_to_project: {
      running: 'Añadiendo al proyecto',
      done: 'Añadido al proyecto',
      failed: 'No se pudo añadir al proyecto',
    },
    suggest_project_move: {
      running: 'Comprobando a qué proyecto pertenece el chat',
      done: 'Proyecto sugerido',
      failed: 'No se pudo sugerir un proyecto',
    },
    search_company_knowledge: {
      running: 'Buscando en el conocimiento de la empresa',
      done: 'Conocimiento de la empresa consultado',
      failed: 'No se pudo buscar en el conocimiento de la empresa',
    },
    read_company_context: {
      running: 'Leyendo el contexto de la empresa',
      done: 'Contexto de la empresa leído',
      failed: 'No se pudo leer el contexto de la empresa',
    },
    find_company_work: {
      running: 'Revisando el trabajo de la empresa',
      done: 'Trabajo de la empresa revisado',
      failed: 'No se pudo revisar el trabajo de la empresa',
    },
    read_company_meeting: {
      running: 'Leyendo una reunión',
      done: 'Reunión leída',
      failed: 'No se pudo leer una reunión',
      counted: 'one:{count} reunión leída|other:{count} reuniones leídas',
    },
    read_company_file: {
      running: 'Leyendo un archivo',
      done: 'Archivo leído',
      failed: 'No se pudo leer un archivo',
      counted: 'one:{count} archivo leído|other:{count} archivos leídos',
    },
    search_company_file: {
      running: 'Buscando en un archivo',
      done: 'Archivo consultado',
      failed: 'No se pudo buscar en un archivo',
      counted: 'one:{count} archivo consultado|other:{count} archivos consultados',
    },
    read_company_source: {
      running: 'Leyendo un documento',
      done: 'Documento leído',
      failed: 'No se pudo leer un documento',
      counted: 'one:{count} documento leído|other:{count} documentos leídos',
    },
    storefront_stores: {
      running: 'Revisando los asistentes de compra',
      done: 'Asistentes de compra revisados',
      failed: 'No se pudieron revisar los asistentes de compra',
    },
    render_storefront_preview: {
      running: 'Mirando el asistente de compra',
      done: 'Asistente de compra visto',
      failed: 'No se pudo ver el asistente de compra',
    },
    storefront_conversations: {
      running: 'Leyendo conversaciones de clientes',
      done: 'Conversaciones de clientes leídas',
      failed: 'No se pudieron leer las conversaciones de clientes',
    },
    read_knowledge_page: {
      running: 'Leyendo una página de conocimiento',
      done: 'Página de conocimiento leída',
      failed: 'No se pudo leer una página de conocimiento',
      counted:
        'one:{count} página de conocimiento leída|other:{count} páginas de conocimiento leídas',
    },
    search_work_results: {
      running: 'Buscando en los resultados guardados',
      done: 'Resultados guardados consultados',
      failed: 'No se pudo buscar en los resultados guardados',
    },
    read_work_result: {
      running: 'Leyendo un resultado guardado',
      done: 'Resultado guardado leído',
      failed: 'No se pudo leer un resultado guardado',
      counted: 'one:{count} resultado guardado leído|other:{count} resultados guardados leídos',
    },
    read_previous_chat: {
      running: 'Leyendo un chat anterior',
      done: 'Chat anterior leído',
      failed: 'No se pudo leer un chat anterior',
      counted: 'one:{count} chat anterior leído|other:{count} chats anteriores leídos',
    },
    read_connected_document: {
      running: 'Leyendo un documento conectado',
      done: 'Documento conectado leído',
      failed: 'No se pudo leer un documento conectado',
      counted: 'one:{count} documento conectado leído|other:{count} documentos conectados leídos',
    },
    read_url: {
      running: 'Leyendo una página web',
      done: 'Página web leída',
      failed: 'No se pudo leer una página web',
      counted: 'one:{count} página web leída|other:{count} páginas web leídas',
    },
    open_web: {
      running: 'Leyendo una página web',
      done: 'Página web leída',
      failed: 'No se pudo leer una página web',
      counted: 'one:{count} página web leída|other:{count} páginas web leídas',
    },
    view_image: {
      running: 'Viendo una imagen',
      done: 'Imagen vista',
      failed: 'No se pudo ver una imagen',
      counted: 'one:{count} imagen vista|other:{count} imágenes vistas',
    },
    read_company_skill: {
      running: 'Comprobando los Skills',
      done: 'Skills comprobados',
      failed: 'No se pudieron comprobar los Skills',
    },
    find_right_skill: {
      running: 'Buscando los Skills adecuados',
      done: 'Skills encontrados',
      failed: 'No se pudieron encontrar Skills',
    },
    list_company_skills: {
      running: 'Comprobando los Skills',
      done: 'Skills comprobados',
      failed: 'No se pudieron comprobar los Skills',
    },
    read_my_settings: {
      running: 'Comprobando tu configuración',
      done: 'Configuración comprobada',
      failed: 'No se pudo comprobar tu configuración',
    },
    read_account_overview: {
      running: 'Comprobando tu espacio de trabajo',
      done: 'Espacio de trabajo comprobado',
      failed: 'No se pudo comprobar tu espacio de trabajo',
    },
    list_team: {
      running: 'Comprobando el equipo',
      done: 'Equipo comprobado',
      failed: 'No se pudo comprobar el equipo',
    },
    read_company_usage: {
      running: 'Comprobando el uso',
      done: 'Uso comprobado',
      failed: 'No se pudo comprobar el uso',
    },
    list_connection_tools: {
      running: 'Comprobando las apps conectadas',
      done: 'Apps conectadas comprobadas',
      failed: 'No se pudieron comprobar las apps conectadas',
    },
    read_connection_action: {
      running: 'Comprobando las apps conectadas',
      done: 'Apps conectadas comprobadas',
      failed: 'No se pudieron comprobar las apps conectadas',
    },
    list_custom_mcp_tools: {
      running: 'Comprobando las apps conectadas',
      done: 'Apps conectadas comprobadas',
      failed: 'No se pudieron comprobar las apps conectadas',
    },
    read_custom_mcp_action: {
      running: 'Comprobando las apps conectadas',
      done: 'Apps conectadas comprobadas',
      failed: 'No se pudieron comprobar las apps conectadas',
    },
  },
  et: {
    // Projects v1.
    remove_from_project: {
      running: 'Eemaldan projektist',
      done: 'Projektist eemaldatud',
      failed: 'Projektist ei saanud eemaldada',
    },
    project_context: {
      running: 'Loen projekti lähteülesannet',
      done: 'Projekti lähteülesanne loetud',
      failed: 'Projekti lähteülesannet ei saanud lugeda',
    },
    read_projects: {
      running: 'Kontrollin projekte',
      done: 'Projektid kontrollitud',
      failed: 'Projekte ei saanud kontrollida',
    },
    draft_project: {
      running: 'Koostan projekti mustandit',
      done: 'Projekti mustand koostatud',
      failed: 'Projekti mustandit ei saanud koostada',
    },
    update_project: {
      running: 'Valmistan ette projekti muudatust',
      done: 'Projekti muudatus ette valmistatud',
      failed: 'Projekti muudatust ei saanud ette valmistada',
    },
    move_chat_to_project: {
      running: 'Teisaldan vestlust',
      done: 'Vestlus teisaldatud',
      failed: 'Vestlust ei saanud teisaldada',
    },
    add_to_project: {
      running: 'Lisan projekti',
      done: 'Lisatud projekti',
      failed: 'Projekti ei saanud lisada',
    },
    suggest_project_move: {
      running: 'Kontrollin, kuhu vestlus kuulub',
      done: 'Projekt soovitatud',
      failed: 'Projekti ei saanud soovitada',
    },
    search_company_knowledge: {
      running: 'Otsin ettevõtte teadmistest',
      done: 'Ettevõtte teadmistest otsitud',
      failed: 'Ettevõtte teadmistest ei õnnestunud otsida',
    },
    read_company_context: {
      running: 'Loen ettevõtte konteksti',
      done: 'Ettevõtte kontekst loetud',
      failed: 'Ettevõtte konteksti ei õnnestunud lugeda',
    },
    find_company_work: {
      running: 'Vaatan läbi ettevõtte töid',
      done: 'Ettevõtte tööd läbi vaadatud',
      failed: 'Ettevõtte töid ei õnnestunud läbi vaadata',
    },
    read_company_meeting: {
      running: 'Loen koosolekut',
      done: 'Koosolek loetud',
      failed: 'Koosolekut ei õnnestunud lugeda',
      counted: 'one:{count} koosolek loetud|other:{count} koosolekut loetud',
    },
    read_company_file: {
      running: 'Loen faili',
      done: 'Fail loetud',
      failed: 'Faili ei õnnestunud lugeda',
      counted: 'one:{count} fail loetud|other:{count} faili loetud',
    },
    search_company_file: {
      running: 'Otsin failist',
      done: 'Failist otsitud',
      failed: 'Failist ei õnnestunud otsida',
      counted: 'one:Otsitud {count} failist|other:Otsitud {count} failist',
    },
    read_company_source: {
      running: 'Loen dokumenti',
      done: 'Dokument loetud',
      failed: 'Dokumenti ei õnnestunud lugeda',
      counted: 'one:{count} dokument loetud|other:{count} dokumenti loetud',
    },
    storefront_stores: {
      running: 'Kontrollin ostuabilisi',
      done: 'Ostuabilised kontrollitud',
      failed: 'Ostuabilisi ei õnnestunud kontrollida',
    },
    render_storefront_preview: {
      running: 'Vaatan ostuabilist',
      done: 'Ostuabilist vaadatud',
      failed: 'Ostuabilist ei õnnestunud vaadata',
    },
    storefront_conversations: {
      running: 'Loen klientide vestlusi',
      done: 'Klientide vestlused loetud',
      failed: 'Klientide vestlusi ei õnnestunud lugeda',
    },
    read_knowledge_page: {
      running: 'Loen teadmiste lehte',
      done: 'Teadmiste leht loetud',
      failed: 'Teadmiste lehte ei õnnestunud lugeda',
      counted: 'one:{count} teadmiste leht loetud|other:{count} teadmiste lehte loetud',
    },
    search_work_results: {
      running: 'Otsin salvestatud tulemustest',
      done: 'Salvestatud tulemustest otsitud',
      failed: 'Salvestatud tulemustest ei õnnestunud otsida',
    },
    read_work_result: {
      running: 'Loen salvestatud tulemust',
      done: 'Salvestatud tulemus loetud',
      failed: 'Salvestatud tulemust ei õnnestunud lugeda',
      counted: 'one:{count} salvestatud tulemus loetud|other:{count} salvestatud tulemust loetud',
    },
    read_previous_chat: {
      running: 'Loen varasemat vestlust',
      done: 'Varasem vestlus loetud',
      failed: 'Varasemat vestlust ei õnnestunud lugeda',
      counted: 'one:{count} varasem vestlus loetud|other:{count} varasemat vestlust loetud',
    },
    read_connected_document: {
      running: 'Loen ühendatud dokumenti',
      done: 'Ühendatud dokument loetud',
      failed: 'Ühendatud dokumenti ei õnnestunud lugeda',
      counted: 'one:{count} ühendatud dokument loetud|other:{count} ühendatud dokumenti loetud',
    },
    read_url: {
      running: 'Loen veebilehte',
      done: 'Veebileht loetud',
      failed: 'Veebilehte ei õnnestunud lugeda',
      counted: 'one:{count} veebileht loetud|other:{count} veebilehte loetud',
    },
    open_web: {
      running: 'Loen veebilehte',
      done: 'Veebileht loetud',
      failed: 'Veebilehte ei õnnestunud lugeda',
      counted: 'one:{count} veebileht loetud|other:{count} veebilehte loetud',
    },
    view_image: {
      running: 'Vaatan pilti',
      done: 'Pilt vaadatud',
      failed: 'Pilti ei õnnestunud vaadata',
      counted: 'one:{count} pilt vaadatud|other:{count} pilti vaadatud',
    },
    read_company_skill: {
      running: 'Kontrollin Skille',
      done: 'Skillid kontrollitud',
      failed: 'Skille ei õnnestunud kontrollida',
    },
    find_right_skill: {
      running: 'Otsin sobivaid Skille',
      done: 'Skillid leitud',
      failed: 'Skille ei õnnestunud leida',
    },
    list_company_skills: {
      running: 'Kontrollin Skille',
      done: 'Skillid kontrollitud',
      failed: 'Skille ei õnnestunud kontrollida',
    },
    read_my_settings: {
      running: 'Kontrollin teie seadeid',
      done: 'Teie seaded kontrollitud',
      failed: 'Teie seadeid ei õnnestunud kontrollida',
    },
    read_account_overview: {
      running: 'Kontrollin teie tööruumi',
      done: 'Teie tööruum kontrollitud',
      failed: 'Teie tööruumi ei õnnestunud kontrollida',
    },
    list_team: {
      running: 'Kontrollin meeskonda',
      done: 'Meeskond kontrollitud',
      failed: 'Meeskonda ei õnnestunud kontrollida',
    },
    read_company_usage: {
      running: 'Kontrollin kasutust',
      done: 'Kasutus kontrollitud',
      failed: 'Kasutust ei õnnestunud kontrollida',
    },
    list_connection_tools: {
      running: 'Kontrollin ühendatud rakendusi',
      done: 'Ühendatud rakendused kontrollitud',
      failed: 'Ühendatud rakendusi ei õnnestunud kontrollida',
    },
    read_connection_action: {
      running: 'Kontrollin ühendatud rakendusi',
      done: 'Ühendatud rakendused kontrollitud',
      failed: 'Ühendatud rakendusi ei õnnestunud kontrollida',
    },
    list_custom_mcp_tools: {
      running: 'Kontrollin ühendatud rakendusi',
      done: 'Ühendatud rakendused kontrollitud',
      failed: 'Ühendatud rakendusi ei õnnestunud kontrollida',
    },
    read_custom_mcp_action: {
      running: 'Kontrollin ühendatud rakendusi',
      done: 'Ühendatud rakendused kontrollitud',
      failed: 'Ühendatud rakendusi ei õnnestunud kontrollida',
    },
  },
  fi: {
    // Projects v1.
    remove_from_project: {
      running: 'Poistetaan projektista',
      done: 'Poistettu projektista',
      failed: 'Projektista ei voitu poistaa',
    },
    project_context: {
      running: 'Luetaan projektin kuvausta',
      done: 'Projektin kuvaus luettu',
      failed: 'Projektin kuvausta ei voitu lukea',
    },
    read_projects: {
      running: 'Tarkistetaan projekteja',
      done: 'Projektit tarkistettu',
      failed: 'Projekteja ei voitu tarkistaa',
    },
    draft_project: {
      running: 'Laaditaan projektin luonnosta',
      done: 'Projektin luonnos laadittu',
      failed: 'Projektin luonnosta ei voitu laatia',
    },
    update_project: {
      running: 'Valmistellaan projektin muutosta',
      done: 'Projektin muutos valmisteltu',
      failed: 'Projektin muutosta ei voitu valmistella',
    },
    move_chat_to_project: {
      running: 'Siirretään keskustelua',
      done: 'Keskustelu siirretty',
      failed: 'Keskustelua ei voitu siirtää',
    },
    add_to_project: {
      running: 'Lisätään projektiin',
      done: 'Lisätty projektiin',
      failed: 'Projektiin ei voitu lisätä',
    },
    suggest_project_move: {
      running: 'Selvitetään, mihin keskustelu kuuluu',
      done: 'Projektia ehdotettu',
      failed: 'Projektia ei voitu ehdottaa',
    },
    search_company_knowledge: {
      running: 'Haetaan yrityksen tietämyksestä',
      done: 'Yrityksen tietämyksestä haettu',
      failed: 'Yrityksen tietämyksestä ei voitu hakea',
    },
    read_company_context: {
      running: 'Luetaan yrityksen kontekstia',
      done: 'Yrityksen konteksti luettu',
      failed: 'Yrityksen kontekstia ei voitu lukea',
    },
    find_company_work: {
      running: 'Käydään läpi yrityksen töitä',
      done: 'Yrityksen työt käyty läpi',
      failed: 'Yrityksen töitä ei voitu käydä läpi',
    },
    read_company_meeting: {
      running: 'Luetaan kokousta',
      done: 'Kokous luettu',
      failed: 'Kokousta ei voitu lukea',
      counted: 'one:{count} kokous luettu|other:{count} kokousta luettu',
    },
    read_company_file: {
      running: 'Luetaan tiedostoa',
      done: 'Tiedosto luettu',
      failed: 'Tiedostoa ei voitu lukea',
      counted: 'one:{count} tiedosto luettu|other:{count} tiedostoa luettu',
    },
    search_company_file: {
      running: 'Haetaan tiedostosta',
      done: 'Tiedostosta haettu',
      failed: 'Tiedostosta ei voitu hakea',
      counted: 'one:Haettu {count} tiedostosta|other:Haettu {count} tiedostosta',
    },
    read_company_source: {
      running: 'Luetaan dokumenttia',
      done: 'Dokumentti luettu',
      failed: 'Dokumenttia ei voitu lukea',
      counted: 'one:{count} dokumentti luettu|other:{count} dokumenttia luettu',
    },
    storefront_stores: {
      running: 'Tarkistetaan ostosavustajia',
      done: 'Ostosavustajat tarkistettu',
      failed: 'Ostosavustajia ei voitu tarkistaa',
    },
    render_storefront_preview: {
      running: 'Katsotaan ostosavustajaa',
      done: 'Ostosavustaja katsottu',
      failed: 'Ostosavustajaa ei voitu katsoa',
    },
    storefront_conversations: {
      running: 'Luetaan asiakkaiden keskusteluja',
      done: 'Asiakkaiden keskustelut luettu',
      failed: 'Asiakkaiden keskusteluja ei voitu lukea',
    },
    read_knowledge_page: {
      running: 'Luetaan tietämyssivua',
      done: 'Tietämyssivu luettu',
      failed: 'Tietämyssivua ei voitu lukea',
      counted: 'one:{count} tietämyssivu luettu|other:{count} tietämyssivua luettu',
    },
    search_work_results: {
      running: 'Haetaan tallennetuista tuloksista',
      done: 'Tallennetuista tuloksista haettu',
      failed: 'Tallennetuista tuloksista ei voitu hakea',
    },
    read_work_result: {
      running: 'Luetaan tallennettua tulosta',
      done: 'Tallennettu tulos luettu',
      failed: 'Tallennettua tulosta ei voitu lukea',
      counted: 'one:{count} tallennettu tulos luettu|other:{count} tallennettua tulosta luettu',
    },
    read_previous_chat: {
      running: 'Luetaan aiempaa chattia',
      done: 'Aiempi chatti luettu',
      failed: 'Aiempaa chattia ei voitu lukea',
      counted: 'one:{count} aiempi chatti luettu|other:{count} aiempaa chattia luettu',
    },
    read_connected_document: {
      running: 'Luetaan yhdistettyä dokumenttia',
      done: 'Yhdistetty dokumentti luettu',
      failed: 'Yhdistettyä dokumenttia ei voitu lukea',
      counted:
        'one:{count} yhdistetty dokumentti luettu|other:{count} yhdistettyä dokumenttia luettu',
    },
    read_url: {
      running: 'Luetaan verkkosivua',
      done: 'Verkkosivu luettu',
      failed: 'Verkkosivua ei voitu lukea',
      counted: 'one:{count} verkkosivu luettu|other:{count} verkkosivua luettu',
    },
    open_web: {
      running: 'Luetaan verkkosivua',
      done: 'Verkkosivu luettu',
      failed: 'Verkkosivua ei voitu lukea',
      counted: 'one:{count} verkkosivu luettu|other:{count} verkkosivua luettu',
    },
    view_image: {
      running: 'Katsotaan kuvaa',
      done: 'Kuva katsottu',
      failed: 'Kuvaa ei voitu katsoa',
      counted: 'one:{count} kuva katsottu|other:{count} kuvaa katsottu',
    },
    read_company_skill: {
      running: 'Tarkistetaan Skillejä',
      done: 'Skillit tarkistettu',
      failed: 'Skillejä ei voitu tarkistaa',
    },
    find_right_skill: {
      running: 'Etsitään sopivia Skillejä',
      done: 'Skillit löydetty',
      failed: 'Skillejä ei voitu etsiä',
    },
    list_company_skills: {
      running: 'Tarkistetaan Skillejä',
      done: 'Skillit tarkistettu',
      failed: 'Skillejä ei voitu tarkistaa',
    },
    read_my_settings: {
      running: 'Tarkistetaan asetuksiasi',
      done: 'Asetuksesi tarkistettu',
      failed: 'Asetuksiasi ei voitu tarkistaa',
    },
    read_account_overview: {
      running: 'Tarkistetaan työtilaasi',
      done: 'Työtilasi tarkistettu',
      failed: 'Työtilaasi ei voitu tarkistaa',
    },
    list_team: {
      running: 'Tarkistetaan tiimiä',
      done: 'Tiimi tarkistettu',
      failed: 'Tiimiä ei voitu tarkistaa',
    },
    read_company_usage: {
      running: 'Tarkistetaan käyttöä',
      done: 'Käyttö tarkistettu',
      failed: 'Käyttöä ei voitu tarkistaa',
    },
    list_connection_tools: {
      running: 'Tarkistetaan yhdistettyjä sovelluksia',
      done: 'Yhdistetyt sovellukset tarkistettu',
      failed: 'Yhdistettyjä sovelluksia ei voitu tarkistaa',
    },
    read_connection_action: {
      running: 'Tarkistetaan yhdistettyjä sovelluksia',
      done: 'Yhdistetyt sovellukset tarkistettu',
      failed: 'Yhdistettyjä sovelluksia ei voitu tarkistaa',
    },
    list_custom_mcp_tools: {
      running: 'Tarkistetaan yhdistettyjä sovelluksia',
      done: 'Yhdistetyt sovellukset tarkistettu',
      failed: 'Yhdistettyjä sovelluksia ei voitu tarkistaa',
    },
    read_custom_mcp_action: {
      running: 'Tarkistetaan yhdistettyjä sovelluksia',
      done: 'Yhdistetyt sovellukset tarkistettu',
      failed: 'Yhdistettyjä sovelluksia ei voitu tarkistaa',
    },
  },
  fr: {
    // Projects v1.
    remove_from_project: {
      running: 'Retrait du projet',
      done: 'Retiré du projet',
      failed: 'Impossible de retirer du projet',
    },
    project_context: {
      running: 'Lecture du brief du projet',
      done: 'Brief du projet lu',
      failed: 'Impossible de lire le brief du projet',
    },
    read_projects: {
      running: 'Vérification des projets',
      done: 'Projets vérifiés',
      failed: 'Impossible de vérifier les projets',
    },
    draft_project: {
      running: 'Préparation du brouillon du projet',
      done: 'Brouillon du projet préparé',
      failed: 'Impossible de préparer le brouillon du projet',
    },
    update_project: {
      running: 'Préparation de la modification du projet',
      done: 'Modification du projet préparée',
      failed: 'Impossible de préparer la modification du projet',
    },
    move_chat_to_project: {
      running: 'Déplacement du chat',
      done: 'Chat déplacé',
      failed: 'Impossible de déplacer le chat',
    },
    add_to_project: {
      running: 'Ajout au projet',
      done: 'Ajouté au projet',
      failed: 'Impossible d’ajouter au projet',
    },
    suggest_project_move: {
      running: 'Recherche du projet de ce chat',
      done: 'Projet suggéré',
      failed: 'Impossible de suggérer un projet',
    },
    search_company_knowledge: {
      running: 'Recherche dans les connaissances de l’entreprise',
      done: 'Connaissances de l’entreprise consultées',
      failed: 'Impossible de rechercher dans les connaissances de l’entreprise',
    },
    read_company_context: {
      running: 'Lecture du contexte de l’entreprise',
      done: 'Contexte de l’entreprise lu',
      failed: 'Impossible de lire le contexte de l’entreprise',
    },
    find_company_work: {
      running: 'Exploration des travaux de l’entreprise',
      done: 'Travaux de l’entreprise parcourus',
      failed: 'Impossible de parcourir les travaux de l’entreprise',
    },
    read_company_meeting: {
      running: 'Lecture d’une réunion',
      done: 'Réunion lue',
      failed: 'Impossible de lire une réunion',
      counted: 'one:{count} réunion lue|other:{count} réunions lues',
    },
    read_company_file: {
      running: 'Lecture d’un fichier',
      done: 'Fichier lu',
      failed: 'Impossible de lire un fichier',
      counted: 'one:{count} fichier lu|other:{count} fichiers lus',
    },
    search_company_file: {
      running: 'Recherche dans un fichier',
      done: 'Fichier examiné',
      failed: 'Impossible de rechercher dans un fichier',
      counted: 'one:{count} fichier examiné|other:{count} fichiers examinés',
    },
    read_company_source: {
      running: 'Lecture d’un document',
      done: 'Document lu',
      failed: 'Impossible de lire un document',
      counted: 'one:{count} document lu|other:{count} documents lus',
    },
    storefront_stores: {
      running: 'Vérification des assistants d’achat',
      done: 'Assistants d’achat vérifiés',
      failed: 'Impossible de vérifier les assistants d’achat',
    },
    render_storefront_preview: {
      running: 'Aperçu de l’assistant d’achat',
      done: 'Assistant d’achat consulté',
      failed: 'Impossible d’afficher l’assistant d’achat',
    },
    storefront_conversations: {
      running: 'Lecture des conversations des clients',
      done: 'Conversations des clients lues',
      failed: 'Impossible de lire les conversations des clients',
    },
    read_knowledge_page: {
      running: 'Lecture d’une page de connaissances',
      done: 'Page de connaissances lue',
      failed: 'Impossible de lire une page de connaissances',
      counted: 'one:{count} page de connaissances lue|other:{count} pages de connaissances lues',
    },
    search_work_results: {
      running: 'Recherche dans les résultats enregistrés',
      done: 'Résultats enregistrés consultés',
      failed: 'Impossible de rechercher dans les résultats enregistrés',
    },
    read_work_result: {
      running: 'Lecture d’un résultat enregistré',
      done: 'Résultat enregistré lu',
      failed: 'Impossible de lire un résultat enregistré',
      counted: 'one:{count} résultat enregistré lu|other:{count} résultats enregistrés lus',
    },
    read_previous_chat: {
      running: 'Lecture d’une discussion précédente',
      done: 'Discussion précédente lue',
      failed: 'Impossible de lire une discussion précédente',
      counted: 'one:{count} discussion précédente lue|other:{count} discussions précédentes lues',
    },
    read_connected_document: {
      running: 'Lecture d’un document connecté',
      done: 'Document connecté lu',
      failed: 'Impossible de lire un document connecté',
      counted: 'one:{count} document connecté lu|other:{count} documents connectés lus',
    },
    read_url: {
      running: 'Lecture d’une page web',
      done: 'Page web lue',
      failed: 'Impossible de lire une page web',
      counted: 'one:{count} page web lue|other:{count} pages web lues',
    },
    open_web: {
      running: 'Lecture d’une page web',
      done: 'Page web lue',
      failed: 'Impossible de lire une page web',
      counted: 'one:{count} page web lue|other:{count} pages web lues',
    },
    view_image: {
      running: 'Examen d’une image',
      done: 'Image examinée',
      failed: 'Impossible d’examiner une image',
      counted: 'one:{count} image examinée|other:{count} images examinées',
    },
    read_company_skill: {
      running: 'Vérification des Skills',
      done: 'Skills vérifiés',
      failed: 'Impossible de vérifier les Skills',
    },
    find_right_skill: {
      running: 'Recherche des Skills adaptés',
      done: 'Skills trouvés',
      failed: 'Impossible de trouver les Skills',
    },
    list_company_skills: {
      running: 'Vérification des Skills',
      done: 'Skills vérifiés',
      failed: 'Impossible de vérifier les Skills',
    },
    read_my_settings: {
      running: 'Vérification de vos paramètres',
      done: 'Paramètres vérifiés',
      failed: 'Impossible de vérifier vos paramètres',
    },
    read_account_overview: {
      running: 'Vérification de votre espace de travail',
      done: 'Espace de travail vérifié',
      failed: 'Impossible de vérifier votre espace de travail',
    },
    list_team: {
      running: 'Vérification de l’équipe',
      done: 'Équipe vérifiée',
      failed: 'Impossible de vérifier l’équipe',
    },
    read_company_usage: {
      running: 'Vérification de l’utilisation',
      done: 'Utilisation vérifiée',
      failed: 'Impossible de vérifier l’utilisation',
    },
    list_connection_tools: {
      running: 'Vérification des applications connectées',
      done: 'Applications connectées vérifiées',
      failed: 'Impossible de vérifier les applications connectées',
    },
    read_connection_action: {
      running: 'Vérification des applications connectées',
      done: 'Applications connectées vérifiées',
      failed: 'Impossible de vérifier les applications connectées',
    },
    list_custom_mcp_tools: {
      running: 'Vérification des applications connectées',
      done: 'Applications connectées vérifiées',
      failed: 'Impossible de vérifier les applications connectées',
    },
    read_custom_mcp_action: {
      running: 'Vérification des applications connectées',
      done: 'Applications connectées vérifiées',
      failed: 'Impossible de vérifier les applications connectées',
    },
  },
  ga: {
    // Projects v1.
    remove_from_project: {
      running: 'Á bhaint as an tionscadal',
      done: 'Bainte as an tionscadal',
      failed: 'Níorbh fhéidir é a bhaint as an tionscadal',
    },
    project_context: {
      running: 'Ag léamh achoimre an tionscadail',
      done: 'Achoimre an tionscadail léite',
      failed: 'Níorbh fhéidir achoimre an tionscadail a léamh',
    },
    read_projects: {
      running: 'Ag seiceáil tionscadal',
      done: 'Tionscadail seiceáilte',
      failed: 'Níorbh fhéidir tionscadail a sheiceáil',
    },
    draft_project: {
      running: 'Ag dréachtú an tionscadail',
      done: 'Tionscadal dréachtaithe',
      failed: 'Níorbh fhéidir an tionscadal a dhréachtú',
    },
    update_project: {
      running: 'Ag ullmhú athrú an tionscadail',
      done: 'Athrú an tionscadail ullmhaithe',
      failed: 'Níorbh fhéidir athrú an tionscadail a ullmhú',
    },
    move_chat_to_project: {
      running: 'Ag bogadh an chomhrá',
      done: 'Comhrá bogtha',
      failed: 'Níorbh fhéidir an comhrá a bhogadh',
    },
    add_to_project: {
      running: 'Ag cur leis an tionscadal',
      done: 'Curtha leis an tionscadal',
      failed: 'Níorbh fhéidir cur leis an tionscadal',
    },
    suggest_project_move: {
      running: 'Ag seiceáil cén tionscadal lena mbaineann an comhrá',
      done: 'Tionscadal molta',
      failed: 'Níorbh fhéidir tionscadal a mholadh',
    },
    search_company_knowledge: {
      running: 'Eolas na cuideachta á chuardach',
      done: 'Eolas na cuideachta cuardaithe',
      failed: 'Níorbh fhéidir eolas na cuideachta a chuardach',
    },
    read_company_context: {
      running: 'Comhthéacs na cuideachta á léamh',
      done: 'Comhthéacs na cuideachta léite',
      failed: 'Níorbh fhéidir comhthéacs na cuideachta a léamh',
    },
    find_company_work: {
      running: 'Obair na cuideachta á hiniúchadh',
      done: 'Obair na cuideachta iniúchta',
      failed: 'Níorbh fhéidir obair na cuideachta a iniúchadh',
    },
    read_company_meeting: {
      running: 'Cruinniú á léamh',
      done: 'Cruinniú léite',
      failed: 'Níorbh fhéidir cruinniú a léamh',
      counted:
        'one:{count} cruinniú léite|two:{count} chruinniú léite|few:{count} chruinniú léite|many:{count} gcruinniú léite|other:{count} cruinniú léite',
    },
    read_company_file: {
      running: 'Comhad á léamh',
      done: 'Comhad léite',
      failed: 'Níorbh fhéidir comhad a léamh',
      counted:
        'one:{count} comhad léite|two:{count} chomhad léite|few:{count} chomhad léite|many:{count} gcomhad léite|other:{count} comhad léite',
    },
    search_company_file: {
      running: 'Comhad á chuardach',
      done: 'Comhad cuardaithe',
      failed: 'Níorbh fhéidir comhad a chuardach',
      counted:
        'one:{count} comhad cuardaithe|two:{count} chomhad cuardaithe|few:{count} chomhad cuardaithe|many:{count} gcomhad cuardaithe|other:{count} comhad cuardaithe',
    },
    read_company_source: {
      running: 'Doiciméad á léamh',
      done: 'Doiciméad léite',
      failed: 'Níorbh fhéidir doiciméad a léamh',
      counted:
        'one:{count} doiciméad léite|two:{count} dhoiciméad léite|few:{count} dhoiciméad léite|many:{count} ndoiciméad léite|other:{count} doiciméad léite',
    },
    storefront_stores: {
      running: 'Cúntóirí siopadóireachta á seiceáil',
      done: 'Cúntóirí siopadóireachta seiceáilte',
      failed: 'Níorbh fhéidir na cúntóirí siopadóireachta a sheiceáil',
    },
    render_storefront_preview: {
      running: 'An cúntóir siopadóireachta á fheiceáil',
      done: 'An cúntóir siopadóireachta feicthe',
      failed: 'Níorbh fhéidir an cúntóir siopadóireachta a fheiceáil',
    },
    storefront_conversations: {
      running: 'Comhráite custaiméirí á léamh',
      done: 'Comhráite custaiméirí léite',
      failed: 'Níorbh fhéidir comhráite custaiméirí a léamh',
    },
    read_knowledge_page: {
      running: 'Leathanach eolais á léamh',
      done: 'Leathanach eolais léite',
      failed: 'Níorbh fhéidir leathanach eolais a léamh',
      counted:
        'one:{count} leathanach eolais léite|two:{count} leathanach eolais léite|few:{count} leathanach eolais léite|many:{count} leathanach eolais léite|other:{count} leathanach eolais léite',
    },
    search_work_results: {
      running: 'Torthaí sábháilte á gcuardach',
      done: 'Torthaí sábháilte cuardaithe',
      failed: 'Níorbh fhéidir torthaí sábháilte a chuardach',
    },
    read_work_result: {
      running: 'Toradh sábháilte á léamh',
      done: 'Toradh sábháilte léite',
      failed: 'Níorbh fhéidir toradh sábháilte a léamh',
      counted:
        'one:{count} toradh sábháilte léite|two:{count} thoradh sábháilte léite|few:{count} thoradh sábháilte léite|many:{count} dtoradh sábháilte léite|other:{count} toradh sábháilte léite',
    },
    read_previous_chat: {
      running: 'Comhrá roimhe seo á léamh',
      done: 'Comhrá roimhe seo léite',
      failed: 'Níorbh fhéidir comhrá roimhe seo a léamh',
      counted:
        'one:{count} comhrá roimhe seo léite|two:{count} chomhrá roimhe seo léite|few:{count} chomhrá roimhe seo léite|many:{count} gcomhrá roimhe seo léite|other:{count} comhrá roimhe seo léite',
    },
    read_connected_document: {
      running: 'Doiciméad nasctha á léamh',
      done: 'Doiciméad nasctha léite',
      failed: 'Níorbh fhéidir doiciméad nasctha a léamh',
      counted:
        'one:{count} doiciméad nasctha léite|two:{count} dhoiciméad nasctha léite|few:{count} dhoiciméad nasctha léite|many:{count} ndoiciméad nasctha léite|other:{count} doiciméad nasctha léite',
    },
    read_url: {
      running: 'Leathanach gréasáin á léamh',
      done: 'Leathanach gréasáin léite',
      failed: 'Níorbh fhéidir leathanach gréasáin a léamh',
      counted:
        'one:{count} leathanach gréasáin léite|two:{count} leathanach gréasáin léite|few:{count} leathanach gréasáin léite|many:{count} leathanach gréasáin léite|other:{count} leathanach gréasáin léite',
    },
    open_web: {
      running: 'Leathanach gréasáin á léamh',
      done: 'Leathanach gréasáin léite',
      failed: 'Níorbh fhéidir leathanach gréasáin a léamh',
      counted:
        'one:{count} leathanach gréasáin léite|two:{count} leathanach gréasáin léite|few:{count} leathanach gréasáin léite|many:{count} leathanach gréasáin léite|other:{count} leathanach gréasáin léite',
    },
    view_image: {
      running: 'Íomhá á scrúdú',
      done: 'Íomhá scrúdaithe',
      failed: 'Níorbh fhéidir íomhá a scrúdú',
      counted:
        'one:{count} íomhá scrúdaithe|two:{count} íomhá scrúdaithe|few:{count} íomhá scrúdaithe|many:{count} n-íomhá scrúdaithe|other:{count} íomhá scrúdaithe',
    },
    read_company_skill: {
      running: 'Skills á seiceáil',
      done: 'Skills seiceáilte',
      failed: 'Níorbh fhéidir Skills a sheiceáil',
    },
    find_right_skill: {
      running: 'Ag aimsiú na Skills cearta',
      done: 'Skills aimsithe',
      failed: 'Níorbh fhéidir Skills a aimsiú',
    },
    list_company_skills: {
      running: 'Skills á seiceáil',
      done: 'Skills seiceáilte',
      failed: 'Níorbh fhéidir Skills a sheiceáil',
    },
    read_my_settings: {
      running: 'Do shocruithe á seiceáil',
      done: 'Socruithe seiceáilte',
      failed: 'Níorbh fhéidir do shocruithe a sheiceáil',
    },
    read_account_overview: {
      running: 'Do spás oibre á sheiceáil',
      done: 'Spás oibre seiceáilte',
      failed: 'Níorbh fhéidir do spás oibre a sheiceáil',
    },
    list_team: {
      running: 'An fhoireann á seiceáil',
      done: 'Foireann seiceáilte',
      failed: 'Níorbh fhéidir an fhoireann a sheiceáil',
    },
    read_company_usage: {
      running: 'An úsáid á seiceáil',
      done: 'Úsáid seiceáilte',
      failed: 'Níorbh fhéidir an úsáid a sheiceáil',
    },
    list_connection_tools: {
      running: 'Aipeanna nasctha á seiceáil',
      done: 'Aipeanna nasctha seiceáilte',
      failed: 'Níorbh fhéidir aipeanna nasctha a sheiceáil',
    },
    read_connection_action: {
      running: 'Aipeanna nasctha á seiceáil',
      done: 'Aipeanna nasctha seiceáilte',
      failed: 'Níorbh fhéidir aipeanna nasctha a sheiceáil',
    },
    list_custom_mcp_tools: {
      running: 'Aipeanna nasctha á seiceáil',
      done: 'Aipeanna nasctha seiceáilte',
      failed: 'Níorbh fhéidir aipeanna nasctha a sheiceáil',
    },
    read_custom_mcp_action: {
      running: 'Aipeanna nasctha á seiceáil',
      done: 'Aipeanna nasctha seiceáilte',
      failed: 'Níorbh fhéidir aipeanna nasctha a sheiceáil',
    },
  },
  hr: {
    // Projects v1.
    remove_from_project: {
      running: 'Uklanjam iz projekta',
      done: 'Uklonjeno iz projekta',
      failed: 'Nije moguće ukloniti iz projekta',
    },
    project_context: {
      running: 'Čitam opis projekta',
      done: 'Opis projekta pročitan',
      failed: 'Opis projekta nije moguće pročitati',
    },
    read_projects: {
      running: 'Provjeravam projekte',
      done: 'Projekti provjereni',
      failed: 'Projekte nije moguće provjeriti',
    },
    draft_project: {
      running: 'Pripremam nacrt projekta',
      done: 'Nacrt projekta pripremljen',
      failed: 'Nacrt projekta nije moguće pripremiti',
    },
    update_project: {
      running: 'Pripremam promjenu projekta',
      done: 'Promjena projekta pripremljena',
      failed: 'Promjenu projekta nije moguće pripremiti',
    },
    move_chat_to_project: {
      running: 'Premještam razgovor',
      done: 'Razgovor premješten',
      failed: 'Razgovor nije moguće premjestiti',
    },
    add_to_project: {
      running: 'Dodajem u projekt',
      done: 'Dodano u projekt',
      failed: 'Nije moguće dodati u projekt',
    },
    suggest_project_move: {
      running: 'Provjeravam kamo razgovor pripada',
      done: 'Predložen projekt',
      failed: 'Projekt nije moguće predložiti',
    },
    search_company_knowledge: {
      running: 'Pretražujem znanje tvrtke',
      done: 'Znanje tvrtke pretraženo',
      failed: 'Znanje tvrtke nije moguće pretražiti',
    },
    read_company_context: {
      running: 'Čitam kontekst tvrtke',
      done: 'Kontekst tvrtke pročitan',
      failed: 'Kontekst tvrtke nije moguće pročitati',
    },
    find_company_work: {
      running: 'Pregledavam radove tvrtke',
      done: 'Radovi tvrtke pregledani',
      failed: 'Radove tvrtke nije moguće pregledati',
    },
    read_company_meeting: {
      running: 'Čitam sastanak',
      done: 'Sastanak pročitan',
      failed: 'Sastanak nije moguće pročitati',
      counted:
        'one:{count} sastanak pročitan|few:{count} sastanka pročitana|other:{count} sastanaka pročitano',
    },
    read_company_file: {
      running: 'Čitam datoteku',
      done: 'Datoteka pročitana',
      failed: 'Datoteku nije moguće pročitati',
      counted:
        'one:{count} datoteka pročitana|few:{count} datoteke pročitane|other:{count} datoteka pročitano',
    },
    search_company_file: {
      running: 'Pretražujem datoteku',
      done: 'Datoteka pretražena',
      failed: 'Datoteku nije moguće pretražiti',
      counted:
        'one:{count} datoteka pretražena|few:{count} datoteke pretražene|other:{count} datoteka pretraženo',
    },
    read_company_source: {
      running: 'Čitam dokument',
      done: 'Dokument pročitan',
      failed: 'Dokument nije moguće pročitati',
      counted:
        'one:{count} dokument pročitan|few:{count} dokumenta pročitana|other:{count} dokumenata pročitano',
    },
    storefront_stores: {
      running: 'Provjeravam asistente za kupnju',
      done: 'Asistenti za kupnju provjereni',
      failed: 'Asistente za kupnju nije moguće provjeriti',
    },
    render_storefront_preview: {
      running: 'Pregledavam asistenta za kupnju',
      done: 'Asistent za kupnju pregledan',
      failed: 'Asistenta za kupnju nije moguće pregledati',
    },
    storefront_conversations: {
      running: 'Čitam razgovore s kupcima',
      done: 'Razgovori s kupcima pročitani',
      failed: 'Razgovore s kupcima nije moguće pročitati',
    },
    read_knowledge_page: {
      running: 'Čitam stranicu znanja',
      done: 'Stranica znanja pročitana',
      failed: 'Stranicu znanja nije moguće pročitati',
      counted:
        'one:{count} stranica znanja pročitana|few:{count} stranice znanja pročitane|other:{count} stranica znanja pročitano',
    },
    search_work_results: {
      running: 'Pretražujem spremljene rezultate',
      done: 'Spremljeni rezultati pretraženi',
      failed: 'Spremljene rezultate nije moguće pretražiti',
    },
    read_work_result: {
      running: 'Čitam spremljeni rezultat',
      done: 'Spremljeni rezultat pročitan',
      failed: 'Spremljeni rezultat nije moguće pročitati',
      counted:
        'one:{count} spremljeni rezultat pročitan|few:{count} spremljena rezultata pročitana|other:{count} spremljenih rezultata pročitano',
    },
    read_previous_chat: {
      running: 'Čitam raniji razgovor',
      done: 'Raniji razgovor pročitan',
      failed: 'Raniji razgovor nije moguće pročitati',
      counted:
        'one:{count} raniji razgovor pročitan|few:{count} ranija razgovora pročitana|other:{count} ranijih razgovora pročitano',
    },
    read_connected_document: {
      running: 'Čitam povezani dokument',
      done: 'Povezani dokument pročitan',
      failed: 'Povezani dokument nije moguće pročitati',
      counted:
        'one:{count} povezani dokument pročitan|few:{count} povezana dokumenta pročitana|other:{count} povezanih dokumenata pročitano',
    },
    read_url: {
      running: 'Čitam web-stranicu',
      done: 'Web-stranica pročitana',
      failed: 'Web-stranicu nije moguće pročitati',
      counted:
        'one:{count} web-stranica pročitana|few:{count} web-stranice pročitane|other:{count} web-stranica pročitano',
    },
    open_web: {
      running: 'Čitam web-stranicu',
      done: 'Web-stranica pročitana',
      failed: 'Web-stranicu nije moguće pročitati',
      counted:
        'one:{count} web-stranica pročitana|few:{count} web-stranice pročitane|other:{count} web-stranica pročitano',
    },
    view_image: {
      running: 'Pregledavam sliku',
      done: 'Slika pregledana',
      failed: 'Sliku nije moguće pregledati',
      counted:
        'one:{count} slika pregledana|few:{count} slike pregledane|other:{count} slika pregledano',
    },
    read_company_skill: {
      running: 'Provjeravam Skillove',
      done: 'Skillovi provjereni',
      failed: 'Skillove nije moguće provjeriti',
    },
    find_right_skill: {
      running: 'Tražim prave Skillove',
      done: 'Skillovi pronađeni',
      failed: 'Skillove nije moguće pronaći',
    },
    list_company_skills: {
      running: 'Provjeravam Skillove',
      done: 'Skillovi provjereni',
      failed: 'Skillove nije moguće provjeriti',
    },
    read_my_settings: {
      running: 'Provjeravam vaše postavke',
      done: 'Postavke provjerene',
      failed: 'Vaše postavke nije moguće provjeriti',
    },
    read_account_overview: {
      running: 'Provjeravam vaš radni prostor',
      done: 'Radni prostor provjeren',
      failed: 'Vaš radni prostor nije moguće provjeriti',
    },
    list_team: {
      running: 'Provjeravam tim',
      done: 'Tim provjeren',
      failed: 'Tim nije moguće provjeriti',
    },
    read_company_usage: {
      running: 'Provjeravam potrošnju',
      done: 'Potrošnja provjerena',
      failed: 'Potrošnju nije moguće provjeriti',
    },
    list_connection_tools: {
      running: 'Provjeravam povezane aplikacije',
      done: 'Povezane aplikacije provjerene',
      failed: 'Povezane aplikacije nije moguće provjeriti',
    },
    read_connection_action: {
      running: 'Provjeravam povezane aplikacije',
      done: 'Povezane aplikacije provjerene',
      failed: 'Povezane aplikacije nije moguće provjeriti',
    },
    list_custom_mcp_tools: {
      running: 'Provjeravam povezane aplikacije',
      done: 'Povezane aplikacije provjerene',
      failed: 'Povezane aplikacije nije moguće provjeriti',
    },
    read_custom_mcp_action: {
      running: 'Provjeravam povezane aplikacije',
      done: 'Povezane aplikacije provjerene',
      failed: 'Povezane aplikacije nije moguće provjeriti',
    },
  },
  hu: {
    // Projects v1.
    remove_from_project: {
      running: 'Eltávolítás a projektből',
      done: 'Eltávolítva a projektből',
      failed: 'Nem sikerült eltávolítani a projektből',
    },
    project_context: {
      running: 'A projekt leírásának olvasása',
      done: 'A projekt leírása elolvasva',
      failed: 'A projekt leírását nem sikerült elolvasni',
    },
    read_projects: {
      running: 'Projektek ellenőrzése',
      done: 'Projektek ellenőrizve',
      failed: 'A projekteket nem sikerült ellenőrizni',
    },
    draft_project: {
      running: 'A projekt vázlatának elkészítése',
      done: 'A projekt vázlata elkészült',
      failed: 'A projekt vázlatát nem sikerült elkészíteni',
    },
    update_project: {
      running: 'A projekt módosításának előkészítése',
      done: 'A projekt módosítása előkészítve',
      failed: 'A projekt módosítását nem sikerült előkészíteni',
    },
    move_chat_to_project: {
      running: 'A csevegés áthelyezése',
      done: 'A csevegés áthelyezve',
      failed: 'A csevegést nem sikerült áthelyezni',
    },
    add_to_project: {
      running: 'Hozzáadás a projekthez',
      done: 'Hozzáadva a projekthez',
      failed: 'Nem sikerült hozzáadni a projekthez',
    },
    suggest_project_move: {
      running: 'Annak ellenőrzése, hová tartozik a csevegés',
      done: 'Projekt javasolva',
      failed: 'Nem sikerült projektet javasolni',
    },
    search_company_knowledge: {
      running: 'Keresés a vállalati tudásban',
      done: 'Vállalati tudás átkeresve',
      failed: 'Nem sikerült keresni a vállalati tudásban',
    },
    read_company_context: {
      running: 'Vállalati kontextus olvasása',
      done: 'Vállalati kontextus beolvasva',
      failed: 'A vállalati kontextust nem sikerült beolvasni',
    },
    find_company_work: {
      running: 'Vállalati munkák áttekintése',
      done: 'Vállalati munkák áttekintve',
      failed: 'A vállalati munkákat nem sikerült áttekinteni',
    },
    read_company_meeting: {
      running: 'Megbeszélés olvasása',
      done: 'Megbeszélés beolvasva',
      failed: 'A megbeszélést nem sikerült beolvasni',
      counted: 'one:{count} megbeszélés beolvasva|other:{count} megbeszélés beolvasva',
    },
    read_company_file: {
      running: 'Fájl olvasása',
      done: 'Fájl beolvasva',
      failed: 'A fájlt nem sikerült beolvasni',
      counted: 'one:{count} fájl beolvasva|other:{count} fájl beolvasva',
    },
    search_company_file: {
      running: 'Keresés egy fájlban',
      done: 'Fájl átkeresve',
      failed: 'Nem sikerült keresni a fájlban',
      counted: 'one:{count} fájl átkeresve|other:{count} fájl átkeresve',
    },
    read_company_source: {
      running: 'Dokumentum olvasása',
      done: 'Dokumentum beolvasva',
      failed: 'A dokumentumot nem sikerült beolvasni',
      counted: 'one:{count} dokumentum beolvasva|other:{count} dokumentum beolvasva',
    },
    storefront_stores: {
      running: 'Vásárlási asszisztensek ellenőrzése',
      done: 'Vásárlási asszisztensek ellenőrizve',
      failed: 'A vásárlási asszisztenseket nem sikerült ellenőrizni',
    },
    render_storefront_preview: {
      running: 'A vásárlási asszisztens megtekintése',
      done: 'Vásárlási asszisztens megtekintve',
      failed: 'A vásárlási asszisztenst nem sikerült megtekinteni',
    },
    storefront_conversations: {
      running: 'Vásárlói beszélgetések olvasása',
      done: 'Vásárlói beszélgetések beolvasva',
      failed: 'A vásárlói beszélgetéseket nem sikerült beolvasni',
    },
    read_knowledge_page: {
      running: 'Tudásoldal olvasása',
      done: 'Tudásoldal beolvasva',
      failed: 'A tudásoldalt nem sikerült beolvasni',
      counted: 'one:{count} tudásoldal beolvasva|other:{count} tudásoldal beolvasva',
    },
    search_work_results: {
      running: 'Keresés a mentett eredmények között',
      done: 'Mentett eredmények átkeresve',
      failed: 'Nem sikerült keresni a mentett eredmények között',
    },
    read_work_result: {
      running: 'Mentett eredmény olvasása',
      done: 'Mentett eredmény beolvasva',
      failed: 'A mentett eredményt nem sikerült beolvasni',
      counted: 'one:{count} mentett eredmény beolvasva|other:{count} mentett eredmény beolvasva',
    },
    read_previous_chat: {
      running: 'Korábbi csevegés olvasása',
      done: 'Korábbi csevegés beolvasva',
      failed: 'A korábbi csevegést nem sikerült beolvasni',
      counted: 'one:{count} korábbi csevegés beolvasva|other:{count} korábbi csevegés beolvasva',
    },
    read_connected_document: {
      running: 'Csatlakoztatott dokumentum olvasása',
      done: 'Csatlakoztatott dokumentum beolvasva',
      failed: 'A csatlakoztatott dokumentumot nem sikerült beolvasni',
      counted:
        'one:{count} csatlakoztatott dokumentum beolvasva|other:{count} csatlakoztatott dokumentum beolvasva',
    },
    read_url: {
      running: 'Weboldal olvasása',
      done: 'Weboldal beolvasva',
      failed: 'A weboldalt nem sikerült beolvasni',
      counted: 'one:{count} weboldal beolvasva|other:{count} weboldal beolvasva',
    },
    open_web: {
      running: 'Weboldal olvasása',
      done: 'Weboldal beolvasva',
      failed: 'A weboldalt nem sikerült beolvasni',
      counted: 'one:{count} weboldal beolvasva|other:{count} weboldal beolvasva',
    },
    view_image: {
      running: 'Kép megtekintése',
      done: 'Kép megtekintve',
      failed: 'A képet nem sikerült megtekinteni',
      counted: 'one:{count} kép megtekintve|other:{count} kép megtekintve',
    },
    read_company_skill: {
      running: 'Skillek ellenőrzése',
      done: 'Skillek ellenőrizve',
      failed: 'A Skilleket nem sikerült ellenőrizni',
    },
    find_right_skill: {
      running: 'A megfelelő Skillek keresése',
      done: 'Skillek megtalálva',
      failed: 'A Skilleket nem sikerült megtalálni',
    },
    list_company_skills: {
      running: 'Skillek ellenőrzése',
      done: 'Skillek ellenőrizve',
      failed: 'A Skilleket nem sikerült ellenőrizni',
    },
    read_my_settings: {
      running: 'Beállítások ellenőrzése',
      done: 'Beállítások ellenőrizve',
      failed: 'A beállításokat nem sikerült ellenőrizni',
    },
    read_account_overview: {
      running: 'Munkaterület ellenőrzése',
      done: 'Munkaterület ellenőrizve',
      failed: 'A munkaterületet nem sikerült ellenőrizni',
    },
    list_team: {
      running: 'Csapat ellenőrzése',
      done: 'Csapat ellenőrizve',
      failed: 'A csapatot nem sikerült ellenőrizni',
    },
    read_company_usage: {
      running: 'Felhasználás ellenőrzése',
      done: 'Felhasználás ellenőrizve',
      failed: 'A felhasználást nem sikerült ellenőrizni',
    },
    list_connection_tools: {
      running: 'Csatlakoztatott alkalmazások ellenőrzése',
      done: 'Csatlakoztatott alkalmazások ellenőrizve',
      failed: 'A csatlakoztatott alkalmazásokat nem sikerült ellenőrizni',
    },
    read_connection_action: {
      running: 'Csatlakoztatott alkalmazások ellenőrzése',
      done: 'Csatlakoztatott alkalmazások ellenőrizve',
      failed: 'A csatlakoztatott alkalmazásokat nem sikerült ellenőrizni',
    },
    list_custom_mcp_tools: {
      running: 'Csatlakoztatott alkalmazások ellenőrzése',
      done: 'Csatlakoztatott alkalmazások ellenőrizve',
      failed: 'A csatlakoztatott alkalmazásokat nem sikerült ellenőrizni',
    },
    read_custom_mcp_action: {
      running: 'Csatlakoztatott alkalmazások ellenőrzése',
      done: 'Csatlakoztatott alkalmazások ellenőrizve',
      failed: 'A csatlakoztatott alkalmazásokat nem sikerült ellenőrizni',
    },
  },
  it: {
    // Projects v1.
    remove_from_project: {
      running: 'Rimozione dal progetto',
      done: 'Rimosso dal progetto',
      failed: 'Impossibile rimuovere dal progetto',
    },
    project_context: {
      running: 'Lettura del brief del progetto',
      done: 'Brief del progetto letto',
      failed: 'Impossibile leggere il brief del progetto',
    },
    read_projects: {
      running: 'Controllo dei progetti',
      done: 'Progetti controllati',
      failed: 'Impossibile controllare i progetti',
    },
    draft_project: {
      running: 'Preparazione della bozza del progetto',
      done: 'Bozza del progetto preparata',
      failed: 'Impossibile preparare la bozza del progetto',
    },
    update_project: {
      running: 'Preparazione della modifica al progetto',
      done: 'Modifica al progetto preparata',
      failed: 'Impossibile preparare la modifica al progetto',
    },
    move_chat_to_project: {
      running: 'Spostamento della chat',
      done: 'Chat spostata',
      failed: 'Impossibile spostare la chat',
    },
    add_to_project: {
      running: 'Aggiunta al progetto',
      done: 'Aggiunto al progetto',
      failed: 'Impossibile aggiungere al progetto',
    },
    suggest_project_move: {
      running: 'Verifica del progetto di questa chat',
      done: 'Progetto suggerito',
      failed: 'Impossibile suggerire un progetto',
    },
    search_company_knowledge: {
      running: 'Ricerca nelle conoscenze aziendali',
      done: 'Conoscenze aziendali consultate',
      failed: 'Impossibile cercare nelle conoscenze aziendali',
    },
    read_company_context: {
      running: 'Lettura del contesto aziendale',
      done: 'Contesto aziendale letto',
      failed: 'Impossibile leggere il contesto aziendale',
    },
    find_company_work: {
      running: 'Consultazione dei lavori aziendali',
      done: 'Lavori aziendali consultati',
      failed: 'Impossibile consultare i lavori aziendali',
    },
    read_company_meeting: {
      running: 'Lettura di una riunione',
      done: 'Riunione letta',
      failed: 'Impossibile leggere una riunione',
      counted: 'one:{count} riunione letta|other:{count} riunioni lette',
    },
    read_company_file: {
      running: 'Lettura di un file',
      done: 'File letto',
      failed: 'Impossibile leggere un file',
      counted: 'one:{count} file letto|other:{count} file letti',
    },
    search_company_file: {
      running: 'Ricerca in un file',
      done: 'File esaminato',
      failed: 'Impossibile cercare in un file',
      counted: 'one:{count} file esaminato|other:{count} file esaminati',
    },
    read_company_source: {
      running: 'Lettura di un documento',
      done: 'Documento letto',
      failed: 'Impossibile leggere un documento',
      counted: 'one:{count} documento letto|other:{count} documenti letti',
    },
    storefront_stores: {
      running: 'Controllo degli assistenti per lo shopping',
      done: 'Assistenti per lo shopping controllati',
      failed: 'Impossibile controllare gli assistenti per lo shopping',
    },
    render_storefront_preview: {
      running: 'Visualizzazione dell’assistente per lo shopping',
      done: 'Assistente per lo shopping visualizzato',
      failed: 'Impossibile visualizzare l’assistente per lo shopping',
    },
    storefront_conversations: {
      running: 'Lettura delle conversazioni dei clienti',
      done: 'Conversazioni dei clienti lette',
      failed: 'Impossibile leggere le conversazioni dei clienti',
    },
    read_knowledge_page: {
      running: 'Lettura di una pagina di conoscenza',
      done: 'Pagina di conoscenza letta',
      failed: 'Impossibile leggere una pagina di conoscenza',
      counted: 'one:{count} pagina di conoscenza letta|other:{count} pagine di conoscenza lette',
    },
    search_work_results: {
      running: 'Ricerca nei risultati salvati',
      done: 'Risultati salvati consultati',
      failed: 'Impossibile cercare nei risultati salvati',
    },
    read_work_result: {
      running: 'Lettura di un risultato salvato',
      done: 'Risultato salvato letto',
      failed: 'Impossibile leggere un risultato salvato',
      counted: 'one:{count} risultato salvato letto|other:{count} risultati salvati letti',
    },
    read_previous_chat: {
      running: 'Lettura di una chat precedente',
      done: 'Chat precedente letta',
      failed: 'Impossibile leggere una chat precedente',
      counted: 'one:{count} chat precedente letta|other:{count} chat precedenti lette',
    },
    read_connected_document: {
      running: 'Lettura di un documento collegato',
      done: 'Documento collegato letto',
      failed: 'Impossibile leggere un documento collegato',
      counted: 'one:{count} documento collegato letto|other:{count} documenti collegati letti',
    },
    read_url: {
      running: 'Lettura di una pagina web',
      done: 'Pagina web letta',
      failed: 'Impossibile leggere una pagina web',
      counted: 'one:{count} pagina web letta|other:{count} pagine web lette',
    },
    open_web: {
      running: 'Lettura di una pagina web',
      done: 'Pagina web letta',
      failed: 'Impossibile leggere una pagina web',
      counted: 'one:{count} pagina web letta|other:{count} pagine web lette',
    },
    view_image: {
      running: 'Esame di un’immagine',
      done: 'Immagine esaminata',
      failed: 'Impossibile esaminare un’immagine',
      counted: 'one:{count} immagine esaminata|other:{count} immagini esaminate',
    },
    read_company_skill: {
      running: 'Verifica delle Skills',
      done: 'Skills verificate',
      failed: 'Impossibile verificare le Skills',
    },
    find_right_skill: {
      running: 'Ricerca delle Skills adatte',
      done: 'Skills trovate',
      failed: 'Impossibile trovare le Skills',
    },
    list_company_skills: {
      running: 'Verifica delle Skills',
      done: 'Skills verificate',
      failed: 'Impossibile verificare le Skills',
    },
    read_my_settings: {
      running: 'Verifica delle tue impostazioni',
      done: 'Impostazioni verificate',
      failed: 'Impossibile verificare le tue impostazioni',
    },
    read_account_overview: {
      running: 'Verifica del tuo spazio di lavoro',
      done: 'Spazio di lavoro verificato',
      failed: 'Impossibile verificare il tuo spazio di lavoro',
    },
    list_team: {
      running: 'Verifica del team',
      done: 'Team verificato',
      failed: 'Impossibile verificare il team',
    },
    read_company_usage: {
      running: 'Verifica dell’utilizzo',
      done: 'Utilizzo verificato',
      failed: 'Impossibile verificare l’utilizzo',
    },
    list_connection_tools: {
      running: 'Verifica delle app collegate',
      done: 'App collegate verificate',
      failed: 'Impossibile verificare le app collegate',
    },
    read_connection_action: {
      running: 'Verifica delle app collegate',
      done: 'App collegate verificate',
      failed: 'Impossibile verificare le app collegate',
    },
    list_custom_mcp_tools: {
      running: 'Verifica delle app collegate',
      done: 'App collegate verificate',
      failed: 'Impossibile verificare le app collegate',
    },
    read_custom_mcp_action: {
      running: 'Verifica delle app collegate',
      done: 'App collegate verificate',
      failed: 'Impossibile verificare le app collegate',
    },
  },
  lt: {
    // Projects v1.
    remove_from_project: {
      running: 'Išimu iš projekto',
      done: 'Išimta iš projekto',
      failed: 'Nepavyko išimti iš projekto',
    },
    project_context: {
      running: 'Skaitau projekto aprašą',
      done: 'Projekto aprašas perskaitytas',
      failed: 'Nepavyko perskaityti projekto aprašo',
    },
    read_projects: {
      running: 'Tikrinu projektus',
      done: 'Projektai patikrinti',
      failed: 'Nepavyko patikrinti projektų',
    },
    draft_project: {
      running: 'Rengiu projekto juodraštį',
      done: 'Projekto juodraštis parengtas',
      failed: 'Nepavyko parengti projekto juodraščio',
    },
    update_project: {
      running: 'Rengiu projekto pakeitimą',
      done: 'Projekto pakeitimas parengtas',
      failed: 'Nepavyko parengti projekto pakeitimo',
    },
    move_chat_to_project: {
      running: 'Perkeliu pokalbį',
      done: 'Pokalbis perkeltas',
      failed: 'Nepavyko perkelti pokalbio',
    },
    add_to_project: {
      running: 'Pridedu prie projekto',
      done: 'Pridėta prie projekto',
      failed: 'Nepavyko pridėti prie projekto',
    },
    suggest_project_move: {
      running: 'Tikrinu, kuriam projektui priklauso pokalbis',
      done: 'Pasiūlytas projektas',
      failed: 'Nepavyko pasiūlyti projekto',
    },
    search_company_knowledge: {
      running: 'Ieškoma įmonės žiniose',
      done: 'Ieškota įmonės žiniose',
      failed: 'Nepavyko ieškoti įmonės žiniose',
    },
    read_company_context: {
      running: 'Skaitomas įmonės kontekstas',
      done: 'Įmonės kontekstas perskaitytas',
      failed: 'Nepavyko perskaityti įmonės konteksto',
    },
    find_company_work: {
      running: 'Peržiūrimi įmonės darbai',
      done: 'Įmonės darbai peržiūrėti',
      failed: 'Nepavyko peržiūrėti įmonės darbų',
    },
    read_company_meeting: {
      running: 'Skaitomas susitikimas',
      done: 'Susitikimas perskaitytas',
      failed: 'Nepavyko perskaityti susitikimo',
      counted:
        'one:{count} susitikimas perskaitytas|few:{count} susitikimai perskaityti|many:{count} susitikimo perskaityta|other:{count} susitikimų perskaityta',
    },
    read_company_file: {
      running: 'Skaitomas failas',
      done: 'Failas perskaitytas',
      failed: 'Nepavyko perskaityti failo',
      counted:
        'one:{count} failas perskaitytas|few:{count} failai perskaityti|many:{count} failo perskaityta|other:{count} failų perskaityta',
    },
    search_company_file: {
      running: 'Ieškoma faile',
      done: 'Ieškota faile',
      failed: 'Nepavyko ieškoti faile',
      counted:
        'one:{count} failas peržiūrėtas|few:{count} failai peržiūrėti|many:{count} failo peržiūrėta|other:{count} failų peržiūrėta',
    },
    read_company_source: {
      running: 'Skaitomas dokumentas',
      done: 'Dokumentas perskaitytas',
      failed: 'Nepavyko perskaityti dokumento',
      counted:
        'one:{count} dokumentas perskaitytas|few:{count} dokumentai perskaityti|many:{count} dokumento perskaityta|other:{count} dokumentų perskaityta',
    },
    storefront_stores: {
      running: 'Tikrinami pirkimo asistentai',
      done: 'Pirkimo asistentai patikrinti',
      failed: 'Nepavyko patikrinti pirkimo asistentų',
    },
    render_storefront_preview: {
      running: 'Peržiūrimas pirkimo asistentas',
      done: 'Pirkimo asistentas peržiūrėtas',
      failed: 'Nepavyko peržiūrėti pirkimo asistento',
    },
    storefront_conversations: {
      running: 'Skaitomi pirkėjų pokalbiai',
      done: 'Pirkėjų pokalbiai perskaityti',
      failed: 'Nepavyko perskaityti pirkėjų pokalbių',
    },
    read_knowledge_page: {
      running: 'Skaitomas žinių puslapis',
      done: 'Žinių puslapis perskaitytas',
      failed: 'Nepavyko perskaityti žinių puslapio',
      counted:
        'one:{count} žinių puslapis perskaitytas|few:{count} žinių puslapiai perskaityti|many:{count} žinių puslapio perskaityta|other:{count} žinių puslapių perskaityta',
    },
    search_work_results: {
      running: 'Ieškoma išsaugotuose rezultatuose',
      done: 'Ieškota išsaugotuose rezultatuose',
      failed: 'Nepavyko ieškoti išsaugotuose rezultatuose',
    },
    read_work_result: {
      running: 'Skaitomas išsaugotas rezultatas',
      done: 'Išsaugotas rezultatas perskaitytas',
      failed: 'Nepavyko perskaityti išsaugoto rezultato',
      counted:
        'one:{count} išsaugotas rezultatas perskaitytas|few:{count} išsaugoti rezultatai perskaityti|many:{count} išsaugoto rezultato perskaityta|other:{count} išsaugotų rezultatų perskaityta',
    },
    read_previous_chat: {
      running: 'Skaitomas ankstesnis pokalbis',
      done: 'Ankstesnis pokalbis perskaitytas',
      failed: 'Nepavyko perskaityti ankstesnio pokalbio',
      counted:
        'one:{count} ankstesnis pokalbis perskaitytas|few:{count} ankstesni pokalbiai perskaityti|many:{count} ankstesnio pokalbio perskaityta|other:{count} ankstesnių pokalbių perskaityta',
    },
    read_connected_document: {
      running: 'Skaitomas prijungtas dokumentas',
      done: 'Prijungtas dokumentas perskaitytas',
      failed: 'Nepavyko perskaityti prijungto dokumento',
      counted:
        'one:{count} prijungtas dokumentas perskaitytas|few:{count} prijungti dokumentai perskaityti|many:{count} prijungto dokumento perskaityta|other:{count} prijungtų dokumentų perskaityta',
    },
    read_url: {
      running: 'Skaitomas tinklalapis',
      done: 'Tinklalapis perskaitytas',
      failed: 'Nepavyko perskaityti tinklalapio',
      counted:
        'one:{count} tinklalapis perskaitytas|few:{count} tinklalapiai perskaityti|many:{count} tinklalapio perskaityta|other:{count} tinklalapių perskaityta',
    },
    open_web: {
      running: 'Skaitomas tinklalapis',
      done: 'Tinklalapis perskaitytas',
      failed: 'Nepavyko perskaityti tinklalapio',
      counted:
        'one:{count} tinklalapis perskaitytas|few:{count} tinklalapiai perskaityti|many:{count} tinklalapio perskaityta|other:{count} tinklalapių perskaityta',
    },
    view_image: {
      running: 'Peržiūrimas vaizdas',
      done: 'Vaizdas peržiūrėtas',
      failed: 'Nepavyko peržiūrėti vaizdo',
      counted:
        'one:{count} vaizdas peržiūrėtas|few:{count} vaizdai peržiūrėti|many:{count} vaizdo peržiūrėta|other:{count} vaizdų peržiūrėta',
    },
    read_company_skill: {
      running: 'Tikrinami Skills',
      done: 'Skills patikrinti',
      failed: 'Nepavyko patikrinti Skills',
    },
    find_right_skill: {
      running: 'Ieškomi tinkami Skills',
      done: 'Skills rasti',
      failed: 'Nepavyko rasti Skills',
    },
    list_company_skills: {
      running: 'Tikrinami Skills',
      done: 'Skills patikrinti',
      failed: 'Nepavyko patikrinti Skills',
    },
    read_my_settings: {
      running: 'Tikrinami jūsų nustatymai',
      done: 'Nustatymai patikrinti',
      failed: 'Nepavyko patikrinti jūsų nustatymų',
    },
    read_account_overview: {
      running: 'Tikrinama jūsų darbo sritis',
      done: 'Darbo sritis patikrinta',
      failed: 'Nepavyko patikrinti jūsų darbo srities',
    },
    list_team: {
      running: 'Tikrinama komanda',
      done: 'Komanda patikrinta',
      failed: 'Nepavyko patikrinti komandos',
    },
    read_company_usage: {
      running: 'Tikrinamas naudojimas',
      done: 'Naudojimas patikrintas',
      failed: 'Nepavyko patikrinti naudojimo',
    },
    list_connection_tools: {
      running: 'Tikrinamos prijungtos programos',
      done: 'Prijungtos programos patikrintos',
      failed: 'Nepavyko patikrinti prijungtų programų',
    },
    read_connection_action: {
      running: 'Tikrinamos prijungtos programos',
      done: 'Prijungtos programos patikrintos',
      failed: 'Nepavyko patikrinti prijungtų programų',
    },
    list_custom_mcp_tools: {
      running: 'Tikrinamos prijungtos programos',
      done: 'Prijungtos programos patikrintos',
      failed: 'Nepavyko patikrinti prijungtų programų',
    },
    read_custom_mcp_action: {
      running: 'Tikrinamos prijungtos programos',
      done: 'Prijungtos programos patikrintos',
      failed: 'Nepavyko patikrinti prijungtų programų',
    },
  },
  lv: {
    // Projects v1.
    remove_from_project: {
      running: 'Izņemu no projekta',
      done: 'Izņemts no projekta',
      failed: 'Neizdevās izņemt no projekta',
    },
    project_context: {
      running: 'Lasu projekta aprakstu',
      done: 'Projekta apraksts izlasīts',
      failed: 'Neizdevās izlasīt projekta aprakstu',
    },
    read_projects: {
      running: 'Pārbaudu projektus',
      done: 'Projekti pārbaudīti',
      failed: 'Neizdevās pārbaudīt projektus',
    },
    draft_project: {
      running: 'Sagatavoju projekta melnrakstu',
      done: 'Projekta melnraksts sagatavots',
      failed: 'Neizdevās sagatavot projekta melnrakstu',
    },
    update_project: {
      running: 'Sagatavoju projekta izmaiņas',
      done: 'Projekta izmaiņas sagatavotas',
      failed: 'Neizdevās sagatavot projekta izmaiņas',
    },
    move_chat_to_project: {
      running: 'Pārvietoju tērzēšanu',
      done: 'Tērzēšana pārvietota',
      failed: 'Neizdevās pārvietot tērzēšanu',
    },
    add_to_project: {
      running: 'Pievienoju projektam',
      done: 'Pievienots projektam',
      failed: 'Neizdevās pievienot projektam',
    },
    suggest_project_move: {
      running: 'Pārbaudu, kuram projektam pieder tērzēšana',
      done: 'Ieteikts projekts',
      failed: 'Neizdevās ieteikt projektu',
    },
    search_company_knowledge: {
      running: 'Meklē uzņēmuma zināšanās',
      done: 'Uzņēmuma zināšanas pārmeklētas',
      failed: 'Uzņēmuma zināšanās neizdevās meklēt',
    },
    read_company_context: {
      running: 'Lasa uzņēmuma kontekstu',
      done: 'Uzņēmuma konteksts izlasīts',
      failed: 'Uzņēmuma kontekstu neizdevās izlasīt',
    },
    find_company_work: {
      running: 'Pārskata uzņēmuma darbus',
      done: 'Uzņēmuma darbi pārskatīti',
      failed: 'Uzņēmuma darbus neizdevās pārskatīt',
    },
    read_company_meeting: {
      running: 'Lasa sanāksmi',
      done: 'Sanāksme izlasīta',
      failed: 'Sanāksmi neizdevās izlasīt',
      counted: 'one:{count} sanāksme izlasīta|other:{count} sanāksmes izlasītas',
    },
    read_company_file: {
      running: 'Lasa failu',
      done: 'Fails izlasīts',
      failed: 'Failu neizdevās izlasīt',
      counted: 'one:{count} fails izlasīts|other:{count} faili izlasīti',
    },
    search_company_file: {
      running: 'Meklē failā',
      done: 'Fails pārmeklēts',
      failed: 'Failā neizdevās meklēt',
      counted: 'one:{count} fails pārmeklēts|other:{count} faili pārmeklēti',
    },
    read_company_source: {
      running: 'Lasa dokumentu',
      done: 'Dokuments izlasīts',
      failed: 'Dokumentu neizdevās izlasīt',
      counted: 'one:{count} dokuments izlasīts|other:{count} dokumenti izlasīti',
    },
    storefront_stores: {
      running: 'Pārbauda iepirkšanās asistentus',
      done: 'Iepirkšanās asistenti pārbaudīti',
      failed: 'Iepirkšanās asistentus neizdevās pārbaudīt',
    },
    render_storefront_preview: {
      running: 'Apskata iepirkšanās asistentu',
      done: 'Iepirkšanās asistents apskatīts',
      failed: 'Iepirkšanās asistentu neizdevās apskatīt',
    },
    storefront_conversations: {
      running: 'Lasa pircēju sarunas',
      done: 'Pircēju sarunas izlasītas',
      failed: 'Pircēju sarunas neizdevās izlasīt',
    },
    read_knowledge_page: {
      running: 'Lasa zināšanu lapu',
      done: 'Zināšanu lapa izlasīta',
      failed: 'Zināšanu lapu neizdevās izlasīt',
      counted: 'one:{count} zināšanu lapa izlasīta|other:{count} zināšanu lapas izlasītas',
    },
    search_work_results: {
      running: 'Meklē saglabātajos rezultātos',
      done: 'Saglabātie rezultāti pārmeklēti',
      failed: 'Saglabātajos rezultātos neizdevās meklēt',
    },
    read_work_result: {
      running: 'Lasa saglabātu rezultātu',
      done: 'Saglabāts rezultāts izlasīts',
      failed: 'Saglabātu rezultātu neizdevās izlasīt',
      counted:
        'one:{count} saglabāts rezultāts izlasīts|other:{count} saglabāti rezultāti izlasīti',
    },
    read_previous_chat: {
      running: 'Lasa iepriekšēju tērzēšanu',
      done: 'Iepriekšēja tērzēšana izlasīta',
      failed: 'Iepriekšēju tērzēšanu neizdevās izlasīt',
      counted:
        'one:{count} iepriekšēja tērzēšana izlasīta|other:{count} iepriekšējas tērzēšanas izlasītas',
    },
    read_connected_document: {
      running: 'Lasa savienotu dokumentu',
      done: 'Savienots dokuments izlasīts',
      failed: 'Savienotu dokumentu neizdevās izlasīt',
      counted:
        'one:{count} savienots dokuments izlasīts|other:{count} savienoti dokumenti izlasīti',
    },
    read_url: {
      running: 'Lasa tīmekļa lapu',
      done: 'Tīmekļa lapa izlasīta',
      failed: 'Tīmekļa lapu neizdevās izlasīt',
      counted: 'one:{count} tīmekļa lapa izlasīta|other:{count} tīmekļa lapas izlasītas',
    },
    open_web: {
      running: 'Lasa tīmekļa lapu',
      done: 'Tīmekļa lapa izlasīta',
      failed: 'Tīmekļa lapu neizdevās izlasīt',
      counted: 'one:{count} tīmekļa lapa izlasīta|other:{count} tīmekļa lapas izlasītas',
    },
    view_image: {
      running: 'Aplūko attēlu',
      done: 'Attēls aplūkots',
      failed: 'Attēlu neizdevās aplūkot',
      counted: 'one:{count} attēls aplūkots|other:{count} attēli aplūkoti',
    },
    read_company_skill: {
      running: 'Pārbauda Skills',
      done: 'Skills pārbaudīti',
      failed: 'Skills neizdevās pārbaudīt',
    },
    find_right_skill: {
      running: 'Meklē piemērotos Skills',
      done: 'Skills atrasti',
      failed: 'Skills neizdevās atrast',
    },
    list_company_skills: {
      running: 'Pārbauda Skills',
      done: 'Skills pārbaudīti',
      failed: 'Skills neizdevās pārbaudīt',
    },
    read_my_settings: {
      running: 'Pārbauda jūsu iestatījumus',
      done: 'Iestatījumi pārbaudīti',
      failed: 'Jūsu iestatījumus neizdevās pārbaudīt',
    },
    read_account_overview: {
      running: 'Pārbauda jūsu darbvietu',
      done: 'Darbvieta pārbaudīta',
      failed: 'Jūsu darbvietu neizdevās pārbaudīt',
    },
    list_team: {
      running: 'Pārbauda komandu',
      done: 'Komanda pārbaudīta',
      failed: 'Komandu neizdevās pārbaudīt',
    },
    read_company_usage: {
      running: 'Pārbauda lietojumu',
      done: 'Lietojums pārbaudīts',
      failed: 'Lietojumu neizdevās pārbaudīt',
    },
    list_connection_tools: {
      running: 'Pārbauda savienotās lietotnes',
      done: 'Savienotās lietotnes pārbaudītas',
      failed: 'Savienotās lietotnes neizdevās pārbaudīt',
    },
    read_connection_action: {
      running: 'Pārbauda savienotās lietotnes',
      done: 'Savienotās lietotnes pārbaudītas',
      failed: 'Savienotās lietotnes neizdevās pārbaudīt',
    },
    list_custom_mcp_tools: {
      running: 'Pārbauda savienotās lietotnes',
      done: 'Savienotās lietotnes pārbaudītas',
      failed: 'Savienotās lietotnes neizdevās pārbaudīt',
    },
    read_custom_mcp_action: {
      running: 'Pārbauda savienotās lietotnes',
      done: 'Savienotās lietotnes pārbaudītas',
      failed: 'Savienotās lietotnes neizdevās pārbaudīt',
    },
  },
  mt: {
    // Projects v1.
    remove_from_project: {
      running: 'Qed inneħħi mill-proġett',
      done: 'Imneħħi mill-proġett',
      failed: 'Ma setax jitneħħa mill-proġett',
    },
    project_context: {
      running: 'Qed naqra d-deskrizzjoni tal-proġett',
      done: 'Id-deskrizzjoni tal-proġett inqrat',
      failed: 'Ma setgħetx tinqara d-deskrizzjoni tal-proġett',
    },
    read_projects: {
      running: 'Qed niċċekkja l-proġetti',
      done: 'Il-proġetti ġew iċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-proġetti',
    },
    draft_project: {
      running: 'Qed nikteb abbozz tal-proġett',
      done: 'Abbozz tal-proġett miktub',
      failed: 'Ma setax jinkiteb abbozz tal-proġett',
    },
    update_project: {
      running: 'Qed nipprepara l-bidla fil-proġett',
      done: 'Il-bidla fil-proġett ġiet ippreparata',
      failed: 'Ma setgħetx tiġi ppreparata l-bidla fil-proġett',
    },
    move_chat_to_project: {
      running: 'Qed niċċaqlaq iċ-chat',
      done: 'Iċ-chat ġie ċċaqlaq',
      failed: 'Ma setax jiċċaqlaq iċ-chat',
    },
    add_to_project: {
      running: 'Qed inżid mal-proġett',
      done: 'Miżjud mal-proġett',
      failed: 'Ma setax jiżdied mal-proġett',
    },
    suggest_project_move: {
      running: 'Qed niċċekkja f’liema proġett jappartjeni ċ-chat',
      done: 'Proġett issuġġerit',
      failed: 'Ma setax jiġi ssuġġerit proġett',
    },
    search_company_knowledge: {
      running: 'Qed ifittex fl-għarfien tal-kumpanija',
      done: 'Għarfien tal-kumpanija mfittex',
      failed: 'Ma setax ifittex fl-għarfien tal-kumpanija',
    },
    read_company_context: {
      running: 'Qed jaqra l-kuntest tal-kumpanija',
      done: 'Kuntest tal-kumpanija moqri',
      failed: 'Ma setax jinqara l-kuntest tal-kumpanija',
    },
    find_company_work: {
      running: 'Qed jeżamina x-xogħol tal-kumpanija',
      done: 'Xogħol tal-kumpanija eżaminat',
      failed: 'Ma setax jiġi eżaminat ix-xogħol tal-kumpanija',
    },
    read_company_meeting: {
      running: 'Qed jaqra laqgħa',
      done: 'Laqgħa moqrija',
      failed: 'Ma setgħetx tinqara laqgħa',
      counted:
        'one:{count} laqgħa moqrija|two:{count} laqgħat moqrija|few:{count} laqgħat moqrija|many:{count} laqgħa moqrija|other:{count} laqgħa moqrija',
    },
    read_company_file: {
      running: 'Qed jaqra fajl',
      done: 'Fajl moqri',
      failed: 'Ma setax jinqara fajl',
      counted:
        'one:{count} fajl moqri|two:{count} fajls moqrija|few:{count} fajls moqrija|many:{count} fajl moqri|other:{count} fajl moqri',
    },
    search_company_file: {
      running: 'Qed ifittex f’fajl',
      done: 'Fajl imfittex',
      failed: 'Ma setax ifittex f’fajl',
      counted:
        'one:{count} fajl imfittex|two:{count} fajls imfittxa|few:{count} fajls imfittxa|many:{count} fajl imfittex|other:{count} fajl imfittex',
    },
    read_company_source: {
      running: 'Qed jaqra dokument',
      done: 'Dokument moqri',
      failed: 'Ma setax jinqara dokument',
      counted:
        'one:{count} dokument moqri|two:{count} dokumenti moqrija|few:{count} dokumenti moqrija|many:{count} dokument moqri|other:{count} dokument moqri',
    },
    storefront_stores: {
      running: 'Qed jiċċekkja l-assistenti tax-xiri',
      done: 'L-assistenti tax-xiri ġew iċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-assistenti tax-xiri',
    },
    render_storefront_preview: {
      running: 'Qed iħares lejn l-assistent tax-xiri',
      done: 'Ħares lejn l-assistent tax-xiri',
      failed: 'Ma setax iħares lejn l-assistent tax-xiri',
    },
    storefront_conversations: {
      running: 'Qed jaqra l-konversazzjonijiet tal-klijenti',
      done: 'Il-konversazzjonijiet tal-klijenti nqraw',
      failed: 'Ma setgħux jinqraw il-konversazzjonijiet tal-klijenti',
    },
    read_knowledge_page: {
      running: 'Qed jaqra paġna tal-għarfien',
      done: 'Paġna tal-għarfien moqrija',
      failed: 'Ma setgħetx tinqara paġna tal-għarfien',
      counted:
        'one:{count} paġna tal-għarfien moqrija|two:{count} paġni tal-għarfien moqrija|few:{count} paġni tal-għarfien moqrija|many:{count} paġna tal-għarfien moqrija|other:{count} paġna tal-għarfien moqrija',
    },
    search_work_results: {
      running: 'Qed ifittex fir-riżultati salvati',
      done: 'Riżultati salvati mfittxa',
      failed: 'Ma setax ifittex fir-riżultati salvati',
    },
    read_work_result: {
      running: 'Qed jaqra riżultat salvat',
      done: 'Riżultat salvat moqri',
      failed: 'Ma setax jinqara riżultat salvat',
      counted:
        'one:{count} riżultat salvat moqri|two:{count} riżultati salvati moqrija|few:{count} riżultati salvati moqrija|many:{count} riżultat salvat moqri|other:{count} riżultat salvat moqri',
    },
    read_previous_chat: {
      running: 'Qed jaqra chat preċedenti',
      done: 'Chat preċedenti moqri',
      failed: 'Ma setax jinqara chat preċedenti',
      counted:
        'one:{count} chat preċedenti moqri|two:{count} chats preċedenti moqrija|few:{count} chats preċedenti moqrija|many:{count} chat preċedenti moqri|other:{count} chat preċedenti moqri',
    },
    read_connected_document: {
      running: 'Qed jaqra dokument konness',
      done: 'Dokument konness moqri',
      failed: 'Ma setax jinqara dokument konness',
      counted:
        'one:{count} dokument konness moqri|two:{count} dokumenti konnessi moqrija|few:{count} dokumenti konnessi moqrija|many:{count} dokument konness moqri|other:{count} dokument konness moqri',
    },
    read_url: {
      running: 'Qed jaqra paġna web',
      done: 'Paġna web moqrija',
      failed: 'Ma setgħetx tinqara paġna web',
      counted:
        'one:{count} paġna web moqrija|two:{count} paġni web moqrija|few:{count} paġni web moqrija|many:{count} paġna web moqrija|other:{count} paġna web moqrija',
    },
    open_web: {
      running: 'Qed jaqra paġna web',
      done: 'Paġna web moqrija',
      failed: 'Ma setgħetx tinqara paġna web',
      counted:
        'one:{count} paġna web moqrija|two:{count} paġni web moqrija|few:{count} paġni web moqrija|many:{count} paġna web moqrija|other:{count} paġna web moqrija',
    },
    view_image: {
      running: 'Qed iħares lejn immaġni',
      done: 'Immaġni eżaminata',
      failed: 'Ma setgħetx tiġi eżaminata immaġni',
      counted:
        'one:{count} immaġni eżaminata|two:{count} immaġnijiet eżaminati|few:{count} immaġnijiet eżaminati|many:{count} immaġni eżaminata|other:{count} immaġni eżaminata',
    },
    read_company_skill: {
      running: 'Qed jiċċekkja l-Skills',
      done: 'Skills iċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-Skills',
    },
    find_right_skill: {
      running: 'Qed isib l-Skills it-tajba',
      done: 'Skills misjuba',
      failed: 'Ma setgħux jinstabu l-Skills',
    },
    list_company_skills: {
      running: 'Qed jiċċekkja l-Skills',
      done: 'Skills iċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-Skills',
    },
    read_my_settings: {
      running: 'Qed jiċċekkja l-issettjar tiegħek',
      done: 'Issettjar iċċekkjat',
      failed: 'Ma setax jiġi ċċekkjat l-issettjar tiegħek',
    },
    read_account_overview: {
      running: 'Qed jiċċekkja l-ispazju tax-xogħol tiegħek',
      done: 'Spazju tax-xogħol iċċekkjat',
      failed: 'Ma setax jiġi ċċekkjat l-ispazju tax-xogħol tiegħek',
    },
    list_team: {
      running: 'Qed jiċċekkja t-tim',
      done: 'Tim iċċekkjat',
      failed: 'Ma setax jiġi ċċekkjat it-tim',
    },
    read_company_usage: {
      running: 'Qed jiċċekkja l-użu',
      done: 'Użu ċċekkjat',
      failed: 'Ma setax jiġi ċċekkjat l-użu',
    },
    list_connection_tools: {
      running: 'Qed jiċċekkja l-apps konnessi',
      done: 'Apps konnessi ċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-apps konnessi',
    },
    read_connection_action: {
      running: 'Qed jiċċekkja l-apps konnessi',
      done: 'Apps konnessi ċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-apps konnessi',
    },
    list_custom_mcp_tools: {
      running: 'Qed jiċċekkja l-apps konnessi',
      done: 'Apps konnessi ċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-apps konnessi',
    },
    read_custom_mcp_action: {
      running: 'Qed jiċċekkja l-apps konnessi',
      done: 'Apps konnessi ċċekkjati',
      failed: 'Ma setgħux jiġu ċċekkjati l-apps konnessi',
    },
  },
  nl: {
    // Projects v1.
    remove_from_project: {
      running: 'Uit het project halen',
      done: 'Uit het project gehaald',
      failed: 'Kon niet uit het project halen',
    },
    project_context: {
      running: 'De projectbriefing lezen',
      done: 'Projectbriefing gelezen',
      failed: 'Kon de projectbriefing niet lezen',
    },
    read_projects: {
      running: 'Projecten bekijken',
      done: 'Projecten bekeken',
      failed: 'Kon de projecten niet bekijken',
    },
    draft_project: {
      running: 'Een concept van het project maken',
      done: 'Concept van het project gemaakt',
      failed: 'Kon geen concept van het project maken',
    },
    update_project: {
      running: 'De projectwijziging voorbereiden',
      done: 'Projectwijziging voorbereid',
      failed: 'Kon de projectwijziging niet voorbereiden',
    },
    move_chat_to_project: {
      running: 'De chat verplaatsen',
      done: 'Chat verplaatst',
      failed: 'Kon de chat niet verplaatsen',
    },
    add_to_project: {
      running: 'Toevoegen aan het project',
      done: 'Toegevoegd aan het project',
      failed: 'Kon niet toevoegen aan het project',
    },
    suggest_project_move: {
      running: 'Nagaan waar deze chat bij hoort',
      done: 'Project voorgesteld',
      failed: 'Kon geen project voorstellen',
    },
    search_company_knowledge: {
      running: 'Bedrijfskennis doorzoeken',
      done: 'Bedrijfskennis doorzocht',
      failed: 'Bedrijfskennis kon niet worden doorzocht',
    },
    read_company_context: {
      running: 'Bedrijfscontext lezen',
      done: 'Bedrijfscontext gelezen',
      failed: 'Bedrijfscontext kon niet worden gelezen',
    },
    find_company_work: {
      running: 'Bedrijfswerk doornemen',
      done: 'Bedrijfswerk doorgenomen',
      failed: 'Bedrijfswerk kon niet worden doorgenomen',
    },
    read_company_meeting: {
      running: 'Vergadering lezen',
      done: 'Vergadering gelezen',
      failed: 'Vergadering kon niet worden gelezen',
      counted: 'one:{count} vergadering gelezen|other:{count} vergaderingen gelezen',
    },
    read_company_file: {
      running: 'Bestand lezen',
      done: 'Bestand gelezen',
      failed: 'Bestand kon niet worden gelezen',
      counted: 'one:{count} bestand gelezen|other:{count} bestanden gelezen',
    },
    search_company_file: {
      running: 'Bestand doorzoeken',
      done: 'Bestand doorzocht',
      failed: 'Bestand kon niet worden doorzocht',
      counted: 'one:{count} bestand doorzocht|other:{count} bestanden doorzocht',
    },
    read_company_source: {
      running: 'Document lezen',
      done: 'Document gelezen',
      failed: 'Document kon niet worden gelezen',
      counted: 'one:{count} document gelezen|other:{count} documenten gelezen',
    },
    storefront_stores: {
      running: 'Winkelassistenten controleren',
      done: 'Winkelassistenten gecontroleerd',
      failed: 'Winkelassistenten konden niet worden gecontroleerd',
    },
    render_storefront_preview: {
      running: 'Winkelassistent bekijken',
      done: 'Winkelassistent bekeken',
      failed: 'Winkelassistent kon niet worden bekeken',
    },
    storefront_conversations: {
      running: 'Klantgesprekken lezen',
      done: 'Klantgesprekken gelezen',
      failed: 'Klantgesprekken konden niet worden gelezen',
    },
    read_knowledge_page: {
      running: 'Kennispagina lezen',
      done: 'Kennispagina gelezen',
      failed: 'Kennispagina kon niet worden gelezen',
      counted: 'one:{count} kennispagina gelezen|other:{count} kennispagina’s gelezen',
    },
    search_work_results: {
      running: 'Opgeslagen resultaten doorzoeken',
      done: 'Opgeslagen resultaten doorzocht',
      failed: 'Opgeslagen resultaten konden niet worden doorzocht',
    },
    read_work_result: {
      running: 'Opgeslagen resultaat lezen',
      done: 'Opgeslagen resultaat gelezen',
      failed: 'Opgeslagen resultaat kon niet worden gelezen',
      counted:
        'one:{count} opgeslagen resultaat gelezen|other:{count} opgeslagen resultaten gelezen',
    },
    read_previous_chat: {
      running: 'Eerdere chat lezen',
      done: 'Eerdere chat gelezen',
      failed: 'Eerdere chat kon niet worden gelezen',
      counted: 'one:{count} eerdere chat gelezen|other:{count} eerdere chats gelezen',
    },
    read_connected_document: {
      running: 'Gekoppeld document lezen',
      done: 'Gekoppeld document gelezen',
      failed: 'Gekoppeld document kon niet worden gelezen',
      counted: 'one:{count} gekoppeld document gelezen|other:{count} gekoppelde documenten gelezen',
    },
    read_url: {
      running: 'Webpagina lezen',
      done: 'Webpagina gelezen',
      failed: 'Webpagina kon niet worden gelezen',
      counted: 'one:{count} webpagina gelezen|other:{count} webpagina’s gelezen',
    },
    open_web: {
      running: 'Webpagina lezen',
      done: 'Webpagina gelezen',
      failed: 'Webpagina kon niet worden gelezen',
      counted: 'one:{count} webpagina gelezen|other:{count} webpagina’s gelezen',
    },
    view_image: {
      running: 'Afbeelding bekijken',
      done: 'Afbeelding bekeken',
      failed: 'Afbeelding kon niet worden bekeken',
      counted: 'one:{count} afbeelding bekeken|other:{count} afbeeldingen bekeken',
    },
    read_company_skill: {
      running: 'Skills controleren',
      done: 'Skills gecontroleerd',
      failed: 'Skills konden niet worden gecontroleerd',
    },
    find_right_skill: {
      running: 'De juiste Skills zoeken',
      done: 'Skills gevonden',
      failed: 'Skills konden niet worden gevonden',
    },
    list_company_skills: {
      running: 'Skills controleren',
      done: 'Skills gecontroleerd',
      failed: 'Skills konden niet worden gecontroleerd',
    },
    read_my_settings: {
      running: 'Je instellingen controleren',
      done: 'Je instellingen gecontroleerd',
      failed: 'Je instellingen konden niet worden gecontroleerd',
    },
    read_account_overview: {
      running: 'Je werkruimte controleren',
      done: 'Je werkruimte gecontroleerd',
      failed: 'Je werkruimte kon niet worden gecontroleerd',
    },
    list_team: {
      running: 'Team controleren',
      done: 'Team gecontroleerd',
      failed: 'Team kon niet worden gecontroleerd',
    },
    read_company_usage: {
      running: 'Gebruik controleren',
      done: 'Gebruik gecontroleerd',
      failed: 'Gebruik kon niet worden gecontroleerd',
    },
    list_connection_tools: {
      running: 'Gekoppelde apps controleren',
      done: 'Gekoppelde apps gecontroleerd',
      failed: 'Gekoppelde apps konden niet worden gecontroleerd',
    },
    read_connection_action: {
      running: 'Gekoppelde apps controleren',
      done: 'Gekoppelde apps gecontroleerd',
      failed: 'Gekoppelde apps konden niet worden gecontroleerd',
    },
    list_custom_mcp_tools: {
      running: 'Gekoppelde apps controleren',
      done: 'Gekoppelde apps gecontroleerd',
      failed: 'Gekoppelde apps konden niet worden gecontroleerd',
    },
    read_custom_mcp_action: {
      running: 'Gekoppelde apps controleren',
      done: 'Gekoppelde apps gecontroleerd',
      failed: 'Gekoppelde apps konden niet worden gecontroleerd',
    },
  },
  pl: {
    // Projects v1.
    remove_from_project: {
      running: 'Usuwam z projektu',
      done: 'Usunięto z projektu',
      failed: 'Nie udało się usunąć z projektu',
    },
    project_context: {
      running: 'Czytam opis projektu',
      done: 'Przeczytano opis projektu',
      failed: 'Nie udało się przeczytać opisu projektu',
    },
    read_projects: {
      running: 'Sprawdzam projekty',
      done: 'Sprawdzono projekty',
      failed: 'Nie udało się sprawdzić projektów',
    },
    draft_project: {
      running: 'Przygotowuję szkic projektu',
      done: 'Przygotowano szkic projektu',
      failed: 'Nie udało się przygotować szkicu projektu',
    },
    update_project: {
      running: 'Przygotowuję zmianę projektu',
      done: 'Przygotowano zmianę projektu',
      failed: 'Nie udało się przygotować zmiany projektu',
    },
    move_chat_to_project: {
      running: 'Przenoszę czat',
      done: 'Przeniesiono czat',
      failed: 'Nie udało się przenieść czatu',
    },
    add_to_project: {
      running: 'Dodaję do projektu',
      done: 'Dodano do projektu',
      failed: 'Nie udało się dodać do projektu',
    },
    suggest_project_move: {
      running: 'Sprawdzam, do którego projektu należy czat',
      done: 'Zaproponowano projekt',
      failed: 'Nie udało się zaproponować projektu',
    },
    search_company_knowledge: {
      running: 'Przeszukiwanie wiedzy firmy',
      done: 'Przeszukano wiedzę firmy',
      failed: 'Nie udało się przeszukać wiedzy firmy',
    },
    read_company_context: {
      running: 'Czytanie kontekstu firmy',
      done: 'Przeczytano kontekst firmy',
      failed: 'Nie udało się przeczytać kontekstu firmy',
    },
    find_company_work: {
      running: 'Przeglądanie pracy firmy',
      done: 'Przejrzano pracę firmy',
      failed: 'Nie udało się przejrzeć pracy firmy',
    },
    read_company_meeting: {
      running: 'Czytanie spotkania',
      done: 'Przeczytano spotkanie',
      failed: 'Nie udało się przeczytać spotkania',
      counted:
        'one:Przeczytano {count} spotkanie|few:Przeczytano {count} spotkania|many:Przeczytano {count} spotkań|other:Przeczytano {count} spotkania',
    },
    read_company_file: {
      running: 'Czytanie pliku',
      done: 'Przeczytano plik',
      failed: 'Nie udało się przeczytać pliku',
      counted:
        'one:Przeczytano {count} plik|few:Przeczytano {count} pliki|many:Przeczytano {count} plików|other:Przeczytano {count} pliku',
    },
    search_company_file: {
      running: 'Przeszukiwanie pliku',
      done: 'Przeszukano plik',
      failed: 'Nie udało się przeszukać pliku',
      counted:
        'one:Przeszukano {count} plik|few:Przeszukano {count} pliki|many:Przeszukano {count} plików|other:Przeszukano {count} pliku',
    },
    read_company_source: {
      running: 'Czytanie dokumentu',
      done: 'Przeczytano dokument',
      failed: 'Nie udało się przeczytać dokumentu',
      counted:
        'one:Przeczytano {count} dokument|few:Przeczytano {count} dokumenty|many:Przeczytano {count} dokumentów|other:Przeczytano {count} dokumentu',
    },
    storefront_stores: {
      running: 'Sprawdzanie asystentów zakupowych',
      done: 'Sprawdzono asystentów zakupowych',
      failed: 'Nie udało się sprawdzić asystentów zakupowych',
    },
    render_storefront_preview: {
      running: 'Oglądanie asystenta zakupowego',
      done: 'Obejrzano asystenta zakupowego',
      failed: 'Nie udało się obejrzeć asystenta zakupowego',
    },
    storefront_conversations: {
      running: 'Czytanie rozmów z klientami',
      done: 'Przeczytano rozmowy z klientami',
      failed: 'Nie udało się przeczytać rozmów z klientami',
    },
    read_knowledge_page: {
      running: 'Czytanie strony wiedzy',
      done: 'Przeczytano stronę wiedzy',
      failed: 'Nie udało się przeczytać strony wiedzy',
      counted:
        'one:Przeczytano {count} stronę wiedzy|few:Przeczytano {count} strony wiedzy|many:Przeczytano {count} stron wiedzy|other:Przeczytano {count} strony wiedzy',
    },
    search_work_results: {
      running: 'Przeszukiwanie zapisanych wyników',
      done: 'Przeszukano zapisane wyniki',
      failed: 'Nie udało się przeszukać zapisanych wyników',
    },
    read_work_result: {
      running: 'Czytanie zapisanego wyniku',
      done: 'Przeczytano zapisany wynik',
      failed: 'Nie udało się przeczytać zapisanego wyniku',
      counted:
        'one:Przeczytano {count} zapisany wynik|few:Przeczytano {count} zapisane wyniki|many:Przeczytano {count} zapisanych wyników|other:Przeczytano {count} zapisanego wyniku',
    },
    read_previous_chat: {
      running: 'Czytanie wcześniejszego czatu',
      done: 'Przeczytano wcześniejszy czat',
      failed: 'Nie udało się przeczytać wcześniejszego czatu',
      counted:
        'one:Przeczytano {count} wcześniejszy czat|few:Przeczytano {count} wcześniejsze czaty|many:Przeczytano {count} wcześniejszych czatów|other:Przeczytano {count} wcześniejszego czatu',
    },
    read_connected_document: {
      running: 'Czytanie połączonego dokumentu',
      done: 'Przeczytano połączony dokument',
      failed: 'Nie udało się przeczytać połączonego dokumentu',
      counted:
        'one:Przeczytano {count} połączony dokument|few:Przeczytano {count} połączone dokumenty|many:Przeczytano {count} połączonych dokumentów|other:Przeczytano {count} połączonego dokumentu',
    },
    read_url: {
      running: 'Czytanie strony internetowej',
      done: 'Przeczytano stronę internetową',
      failed: 'Nie udało się przeczytać strony internetowej',
      counted:
        'one:Przeczytano {count} stronę internetową|few:Przeczytano {count} strony internetowe|many:Przeczytano {count} stron internetowych|other:Przeczytano {count} strony internetowej',
    },
    open_web: {
      running: 'Czytanie strony internetowej',
      done: 'Przeczytano stronę internetową',
      failed: 'Nie udało się przeczytać strony internetowej',
      counted:
        'one:Przeczytano {count} stronę internetową|few:Przeczytano {count} strony internetowe|many:Przeczytano {count} stron internetowych|other:Przeczytano {count} strony internetowej',
    },
    view_image: {
      running: 'Oglądanie obrazu',
      done: 'Obejrzano obraz',
      failed: 'Nie udało się obejrzeć obrazu',
      counted:
        'one:Obejrzano {count} obraz|few:Obejrzano {count} obrazy|many:Obejrzano {count} obrazów|other:Obejrzano {count} obrazu',
    },
    read_company_skill: {
      running: 'Sprawdzanie Skilli',
      done: 'Sprawdzono Skille',
      failed: 'Nie udało się sprawdzić Skilli',
    },
    find_right_skill: {
      running: 'Szukanie odpowiednich Skilli',
      done: 'Znaleziono Skille',
      failed: 'Nie udało się znaleźć Skilli',
    },
    list_company_skills: {
      running: 'Sprawdzanie Skilli',
      done: 'Sprawdzono Skille',
      failed: 'Nie udało się sprawdzić Skilli',
    },
    read_my_settings: {
      running: 'Sprawdzanie Twoich ustawień',
      done: 'Sprawdzono Twoje ustawienia',
      failed: 'Nie udało się sprawdzić Twoich ustawień',
    },
    read_account_overview: {
      running: 'Sprawdzanie Twojego obszaru roboczego',
      done: 'Sprawdzono Twój obszar roboczy',
      failed: 'Nie udało się sprawdzić Twojego obszaru roboczego',
    },
    list_team: {
      running: 'Sprawdzanie zespołu',
      done: 'Sprawdzono zespół',
      failed: 'Nie udało się sprawdzić zespołu',
    },
    read_company_usage: {
      running: 'Sprawdzanie użycia',
      done: 'Sprawdzono użycie',
      failed: 'Nie udało się sprawdzić użycia',
    },
    list_connection_tools: {
      running: 'Sprawdzanie połączonych aplikacji',
      done: 'Sprawdzono połączone aplikacje',
      failed: 'Nie udało się sprawdzić połączonych aplikacji',
    },
    read_connection_action: {
      running: 'Sprawdzanie połączonych aplikacji',
      done: 'Sprawdzono połączone aplikacje',
      failed: 'Nie udało się sprawdzić połączonych aplikacji',
    },
    list_custom_mcp_tools: {
      running: 'Sprawdzanie połączonych aplikacji',
      done: 'Sprawdzono połączone aplikacje',
      failed: 'Nie udało się sprawdzić połączonych aplikacji',
    },
    read_custom_mcp_action: {
      running: 'Sprawdzanie połączonych aplikacji',
      done: 'Sprawdzono połączone aplikacje',
      failed: 'Nie udało się sprawdzić połączonych aplikacji',
    },
  },
  pt: {
    // Projects v1.
    remove_from_project: {
      running: 'A retirar do projeto',
      done: 'Retirado do projeto',
      failed: 'Não foi possível retirar do projeto',
    },
    project_context: {
      running: 'A ler o briefing do projeto',
      done: 'Briefing do projeto lido',
      failed: 'Não foi possível ler o briefing do projeto',
    },
    read_projects: {
      running: 'A verificar projetos',
      done: 'Projetos verificados',
      failed: 'Não foi possível verificar os projetos',
    },
    draft_project: {
      running: 'A preparar o rascunho do projeto',
      done: 'Rascunho do projeto preparado',
      failed: 'Não foi possível preparar o rascunho do projeto',
    },
    update_project: {
      running: 'A preparar a alteração do projeto',
      done: 'Alteração do projeto preparada',
      failed: 'Não foi possível preparar a alteração do projeto',
    },
    move_chat_to_project: {
      running: 'A mover o chat',
      done: 'Chat movido',
      failed: 'Não foi possível mover o chat',
    },
    add_to_project: {
      running: 'A adicionar ao projeto',
      done: 'Adicionado ao projeto',
      failed: 'Não foi possível adicionar ao projeto',
    },
    suggest_project_move: {
      running: 'A verificar a que projeto pertence o chat',
      done: 'Projeto sugerido',
      failed: 'Não foi possível sugerir um projeto',
    },
    search_company_knowledge: {
      running: 'A pesquisar no conhecimento da empresa',
      done: 'Conhecimento da empresa pesquisado',
      failed: 'Não foi possível pesquisar no conhecimento da empresa',
    },
    read_company_context: {
      running: 'A ler o contexto da empresa',
      done: 'Contexto da empresa lido',
      failed: 'Não foi possível ler o contexto da empresa',
    },
    find_company_work: {
      running: 'A analisar o trabalho da empresa',
      done: 'Trabalho da empresa analisado',
      failed: 'Não foi possível analisar o trabalho da empresa',
    },
    read_company_meeting: {
      running: 'A ler uma reunião',
      done: 'Reunião lida',
      failed: 'Não foi possível ler uma reunião',
      counted:
        'one:{count} reunião lida|many:{count} de reuniões lidas|other:{count} reuniões lidas',
    },
    read_company_file: {
      running: 'A ler um ficheiro',
      done: 'Ficheiro lido',
      failed: 'Não foi possível ler um ficheiro',
      counted:
        'one:{count} ficheiro lido|many:{count} de ficheiros lidos|other:{count} ficheiros lidos',
    },
    search_company_file: {
      running: 'A pesquisar num ficheiro',
      done: 'Ficheiro pesquisado',
      failed: 'Não foi possível pesquisar num ficheiro',
      counted:
        'one:{count} ficheiro pesquisado|many:{count} de ficheiros pesquisados|other:{count} ficheiros pesquisados',
    },
    read_company_source: {
      running: 'A ler um documento',
      done: 'Documento lido',
      failed: 'Não foi possível ler um documento',
      counted:
        'one:{count} documento lido|many:{count} de documentos lidos|other:{count} documentos lidos',
    },
    storefront_stores: {
      running: 'A verificar os assistentes de compras',
      done: 'Assistentes de compras verificados',
      failed: 'Não foi possível verificar os assistentes de compras',
    },
    render_storefront_preview: {
      running: 'A ver o assistente de compras',
      done: 'Assistente de compras visto',
      failed: 'Não foi possível ver o assistente de compras',
    },
    storefront_conversations: {
      running: 'A ler conversas de clientes',
      done: 'Conversas de clientes lidas',
      failed: 'Não foi possível ler as conversas de clientes',
    },
    read_knowledge_page: {
      running: 'A ler uma página de conhecimento',
      done: 'Página de conhecimento lida',
      failed: 'Não foi possível ler uma página de conhecimento',
      counted:
        'one:{count} página de conhecimento lida|many:{count} de páginas de conhecimento lidas|other:{count} páginas de conhecimento lidas',
    },
    search_work_results: {
      running: 'A pesquisar nos resultados guardados',
      done: 'Resultados guardados pesquisados',
      failed: 'Não foi possível pesquisar nos resultados guardados',
    },
    read_work_result: {
      running: 'A ler um resultado guardado',
      done: 'Resultado guardado lido',
      failed: 'Não foi possível ler um resultado guardado',
      counted:
        'one:{count} resultado guardado lido|many:{count} de resultados guardados lidos|other:{count} resultados guardados lidos',
    },
    read_previous_chat: {
      running: 'A ler uma conversa anterior',
      done: 'Conversa anterior lida',
      failed: 'Não foi possível ler uma conversa anterior',
      counted:
        'one:{count} conversa anterior lida|many:{count} de conversas anteriores lidas|other:{count} conversas anteriores lidas',
    },
    read_connected_document: {
      running: 'A ler um documento ligado',
      done: 'Documento ligado lido',
      failed: 'Não foi possível ler um documento ligado',
      counted:
        'one:{count} documento ligado lido|many:{count} de documentos ligados lidos|other:{count} documentos ligados lidos',
    },
    read_url: {
      running: 'A ler uma página web',
      done: 'Página web lida',
      failed: 'Não foi possível ler uma página web',
      counted:
        'one:{count} página web lida|many:{count} de páginas web lidas|other:{count} páginas web lidas',
    },
    open_web: {
      running: 'A ler uma página web',
      done: 'Página web lida',
      failed: 'Não foi possível ler uma página web',
      counted:
        'one:{count} página web lida|many:{count} de páginas web lidas|other:{count} páginas web lidas',
    },
    view_image: {
      running: 'A ver uma imagem',
      done: 'Imagem vista',
      failed: 'Não foi possível ver uma imagem',
      counted:
        'one:{count} imagem vista|many:{count} de imagens vistas|other:{count} imagens vistas',
    },
    read_company_skill: {
      running: 'A verificar as Skills',
      done: 'Skills verificadas',
      failed: 'Não foi possível verificar as Skills',
    },
    find_right_skill: {
      running: 'A procurar as Skills certas',
      done: 'Skills encontradas',
      failed: 'Não foi possível encontrar as Skills',
    },
    list_company_skills: {
      running: 'A verificar as Skills',
      done: 'Skills verificadas',
      failed: 'Não foi possível verificar as Skills',
    },
    read_my_settings: {
      running: 'A verificar as suas definições',
      done: 'Definições verificadas',
      failed: 'Não foi possível verificar as suas definições',
    },
    read_account_overview: {
      running: 'A verificar o seu espaço de trabalho',
      done: 'Espaço de trabalho verificado',
      failed: 'Não foi possível verificar o seu espaço de trabalho',
    },
    list_team: {
      running: 'A verificar a equipa',
      done: 'Equipa verificada',
      failed: 'Não foi possível verificar a equipa',
    },
    read_company_usage: {
      running: 'A verificar a utilização',
      done: 'Utilização verificada',
      failed: 'Não foi possível verificar a utilização',
    },
    list_connection_tools: {
      running: 'A verificar as aplicações ligadas',
      done: 'Aplicações ligadas verificadas',
      failed: 'Não foi possível verificar as aplicações ligadas',
    },
    read_connection_action: {
      running: 'A verificar as aplicações ligadas',
      done: 'Aplicações ligadas verificadas',
      failed: 'Não foi possível verificar as aplicações ligadas',
    },
    list_custom_mcp_tools: {
      running: 'A verificar as aplicações ligadas',
      done: 'Aplicações ligadas verificadas',
      failed: 'Não foi possível verificar as aplicações ligadas',
    },
    read_custom_mcp_action: {
      running: 'A verificar as aplicações ligadas',
      done: 'Aplicações ligadas verificadas',
      failed: 'Não foi possível verificar as aplicações ligadas',
    },
  },
  ro: {
    // Projects v1.
    remove_from_project: {
      running: 'Scot din proiect',
      done: 'Scos din proiect',
      failed: 'Nu s-a putut scoate din proiect',
    },
    project_context: {
      running: 'Citesc descrierea proiectului',
      done: 'Descrierea proiectului a fost citită',
      failed: 'Descrierea proiectului nu a putut fi citită',
    },
    read_projects: {
      running: 'Verific proiectele',
      done: 'Proiectele au fost verificate',
      failed: 'Proiectele nu au putut fi verificate',
    },
    draft_project: {
      running: 'Pregătesc ciorna proiectului',
      done: 'Ciorna proiectului a fost pregătită',
      failed: 'Ciorna proiectului nu a putut fi pregătită',
    },
    update_project: {
      running: 'Pregătesc modificarea proiectului',
      done: 'Modificarea proiectului a fost pregătită',
      failed: 'Modificarea proiectului nu a putut fi pregătită',
    },
    move_chat_to_project: {
      running: 'Mut conversația',
      done: 'Conversația a fost mutată',
      failed: 'Conversația nu a putut fi mutată',
    },
    add_to_project: {
      running: 'Adaug la proiect',
      done: 'Adăugat la proiect',
      failed: 'Nu s-a putut adăuga la proiect',
    },
    suggest_project_move: {
      running: 'Verific unde aparține conversația',
      done: 'Proiect sugerat',
      failed: 'Nu s-a putut sugera un proiect',
    },
    search_company_knowledge: {
      running: 'Se caută în cunoștințele companiei',
      done: 'S-a căutat în cunoștințele companiei',
      failed: 'Nu s-a putut căuta în cunoștințele companiei',
    },
    read_company_context: {
      running: 'Se citește contextul companiei',
      done: 'Contextul companiei a fost citit',
      failed: 'Contextul companiei nu a putut fi citit',
    },
    find_company_work: {
      running: 'Se analizează activitatea companiei',
      done: 'Activitatea companiei a fost analizată',
      failed: 'Activitatea companiei nu a putut fi analizată',
    },
    read_company_meeting: {
      running: 'Se citește o întâlnire',
      done: 'Întâlnire citită',
      failed: 'Întâlnirea nu a putut fi citită',
      counted:
        'one:{count} întâlnire citită|few:{count} întâlniri citite|other:{count} de întâlniri citite',
    },
    read_company_file: {
      running: 'Se citește un fișier',
      done: 'Fișier citit',
      failed: 'Fișierul nu a putut fi citit',
      counted:
        'one:{count} fișier citit|few:{count} fișiere citite|other:{count} de fișiere citite',
    },
    search_company_file: {
      running: 'Se caută într-un fișier',
      done: 'S-a căutat într-un fișier',
      failed: 'Nu s-a putut căuta în fișier',
      counted:
        'one:S-a căutat în {count} fișier|few:S-a căutat în {count} fișiere|other:S-a căutat în {count} de fișiere',
    },
    read_company_source: {
      running: 'Se citește un document',
      done: 'Document citit',
      failed: 'Documentul nu a putut fi citit',
      counted:
        'one:{count} document citit|few:{count} documente citite|other:{count} de documente citite',
    },
    storefront_stores: {
      running: 'Se verifică asistenții de cumpărături',
      done: 'Asistenți de cumpărături verificați',
      failed: 'Asistenții de cumpărături nu au putut fi verificați',
    },
    render_storefront_preview: {
      running: 'Se previzualizează asistentul de cumpărături',
      done: 'Asistent de cumpărături previzualizat',
      failed: 'Asistentul de cumpărături nu a putut fi previzualizat',
    },
    storefront_conversations: {
      running: 'Se citesc conversațiile clienților',
      done: 'Conversațiile clienților citite',
      failed: 'Conversațiile clienților nu au putut fi citite',
    },
    read_knowledge_page: {
      running: 'Se citește o pagină de cunoștințe',
      done: 'Pagină de cunoștințe citită',
      failed: 'Pagina de cunoștințe nu a putut fi citită',
      counted:
        'one:{count} pagină de cunoștințe citită|few:{count} pagini de cunoștințe citite|other:{count} de pagini de cunoștințe citite',
    },
    search_work_results: {
      running: 'Se caută în rezultatele salvate',
      done: 'S-a căutat în rezultatele salvate',
      failed: 'Nu s-a putut căuta în rezultatele salvate',
    },
    read_work_result: {
      running: 'Se citește un rezultat salvat',
      done: 'Rezultat salvat citit',
      failed: 'Rezultatul salvat nu a putut fi citit',
      counted:
        'one:{count} rezultat salvat citit|few:{count} rezultate salvate citite|other:{count} de rezultate salvate citite',
    },
    read_previous_chat: {
      running: 'Se citește un chat anterior',
      done: 'Chat anterior citit',
      failed: 'Chatul anterior nu a putut fi citit',
      counted:
        'one:{count} chat anterior citit|few:{count} chaturi anterioare citite|other:{count} de chaturi anterioare citite',
    },
    read_connected_document: {
      running: 'Se citește un document conectat',
      done: 'Document conectat citit',
      failed: 'Documentul conectat nu a putut fi citit',
      counted:
        'one:{count} document conectat citit|few:{count} documente conectate citite|other:{count} de documente conectate citite',
    },
    read_url: {
      running: 'Se citește o pagină web',
      done: 'Pagină web citită',
      failed: 'Pagina web nu a putut fi citită',
      counted:
        'one:{count} pagină web citită|few:{count} pagini web citite|other:{count} de pagini web citite',
    },
    open_web: {
      running: 'Se citește o pagină web',
      done: 'Pagină web citită',
      failed: 'Pagina web nu a putut fi citită',
      counted:
        'one:{count} pagină web citită|few:{count} pagini web citite|other:{count} de pagini web citite',
    },
    view_image: {
      running: 'Se analizează o imagine',
      done: 'Imagine analizată',
      failed: 'Imaginea nu a putut fi analizată',
      counted:
        'one:{count} imagine analizată|few:{count} imagini analizate|other:{count} de imagini analizate',
    },
    read_company_skill: {
      running: 'Se verifică Skill-urile',
      done: 'Skill-urile au fost verificate',
      failed: 'Skill-urile nu au putut fi verificate',
    },
    find_right_skill: {
      running: 'Se caută Skill-urile potrivite',
      done: 'Skill-urile au fost găsite',
      failed: 'Skill-urile nu au putut fi găsite',
    },
    list_company_skills: {
      running: 'Se verifică Skill-urile',
      done: 'Skill-urile au fost verificate',
      failed: 'Skill-urile nu au putut fi verificate',
    },
    read_my_settings: {
      running: 'Se verifică setările dvs.',
      done: 'Setările dvs. au fost verificate',
      failed: 'Setările dvs. nu au putut fi verificate',
    },
    read_account_overview: {
      running: 'Se verifică spațiul de lucru',
      done: 'Spațiul de lucru a fost verificat',
      failed: 'Spațiul de lucru nu a putut fi verificat',
    },
    list_team: {
      running: 'Se verifică echipa',
      done: 'Echipa a fost verificată',
      failed: 'Echipa nu a putut fi verificată',
    },
    read_company_usage: {
      running: 'Se verifică utilizarea',
      done: 'Utilizarea a fost verificată',
      failed: 'Utilizarea nu a putut fi verificată',
    },
    list_connection_tools: {
      running: 'Se verifică aplicațiile conectate',
      done: 'Aplicațiile conectate au fost verificate',
      failed: 'Aplicațiile conectate nu au putut fi verificate',
    },
    read_connection_action: {
      running: 'Se verifică aplicațiile conectate',
      done: 'Aplicațiile conectate au fost verificate',
      failed: 'Aplicațiile conectate nu au putut fi verificate',
    },
    list_custom_mcp_tools: {
      running: 'Se verifică aplicațiile conectate',
      done: 'Aplicațiile conectate au fost verificate',
      failed: 'Aplicațiile conectate nu au putut fi verificate',
    },
    read_custom_mcp_action: {
      running: 'Se verifică aplicațiile conectate',
      done: 'Aplicațiile conectate au fost verificate',
      failed: 'Aplicațiile conectate nu au putut fi verificate',
    },
  },
  sk: {
    // Projects v1.
    remove_from_project: {
      running: 'Odoberám z projektu',
      done: 'Odobraté z projektu',
      failed: 'Z projektu sa nepodarilo odobrať',
    },
    project_context: {
      running: 'Čítam zadanie projektu',
      done: 'Zadanie projektu prečítané',
      failed: 'Zadanie projektu sa nepodarilo prečítať',
    },
    read_projects: {
      running: 'Kontrolujem projekty',
      done: 'Projekty skontrolované',
      failed: 'Projekty sa nepodarilo skontrolovať',
    },
    draft_project: {
      running: 'Pripravujem návrh projektu',
      done: 'Návrh projektu pripravený',
      failed: 'Návrh projektu sa nepodarilo pripraviť',
    },
    update_project: {
      running: 'Pripravujem zmenu projektu',
      done: 'Zmena projektu pripravená',
      failed: 'Zmenu projektu sa nepodarilo pripraviť',
    },
    move_chat_to_project: {
      running: 'Presúvam chat',
      done: 'Chat presunutý',
      failed: 'Chat sa nepodarilo presunúť',
    },
    add_to_project: {
      running: 'Pridávam do projektu',
      done: 'Pridané do projektu',
      failed: 'Do projektu sa nepodarilo pridať',
    },
    suggest_project_move: {
      running: 'Zisťujem, kam chat patrí',
      done: 'Navrhnutý projekt',
      failed: 'Projekt sa nepodarilo navrhnúť',
    },
    search_company_knowledge: {
      running: 'Prehľadávam firemné znalosti',
      done: 'Firemné znalosti prehľadané',
      failed: 'Firemné znalosti sa nepodarilo prehľadať',
    },
    read_company_context: {
      running: 'Čítam firemný kontext',
      done: 'Firemný kontext prečítaný',
      failed: 'Firemný kontext sa nepodarilo prečítať',
    },
    find_company_work: {
      running: 'Prezerám prácu firmy',
      done: 'Práca firmy prezretá',
      failed: 'Prácu firmy sa nepodarilo prezrieť',
    },
    read_company_meeting: {
      running: 'Čítam stretnutie',
      done: 'Stretnutie prečítané',
      failed: 'Stretnutie sa nepodarilo prečítať',
      counted:
        'one:Prečítané {count} stretnutie|few:Prečítané {count} stretnutia|many:Prečítaného {count} stretnutia|other:Prečítaných {count} stretnutí',
    },
    read_company_file: {
      running: 'Čítam súbor',
      done: 'Súbor prečítaný',
      failed: 'Súbor sa nepodarilo prečítať',
      counted:
        'one:Prečítaný {count} súbor|few:Prečítané {count} súbory|many:Prečítaného {count} súboru|other:Prečítaných {count} súborov',
    },
    search_company_file: {
      running: 'Prehľadávam súbor',
      done: 'Súbor prehľadaný',
      failed: 'Súbor sa nepodarilo prehľadať',
      counted:
        'one:Prehľadaný {count} súbor|few:Prehľadané {count} súbory|many:Prehľadaného {count} súboru|other:Prehľadaných {count} súborov',
    },
    read_company_source: {
      running: 'Čítam dokument',
      done: 'Dokument prečítaný',
      failed: 'Dokument sa nepodarilo prečítať',
      counted:
        'one:Prečítaný {count} dokument|few:Prečítané {count} dokumenty|many:Prečítaného {count} dokumentu|other:Prečítaných {count} dokumentov',
    },
    storefront_stores: {
      running: 'Kontrolujem nákupných asistentov',
      done: 'Nákupní asistenti skontrolovaní',
      failed: 'Nákupných asistentov sa nepodarilo skontrolovať',
    },
    render_storefront_preview: {
      running: 'Prezerám nákupného asistenta',
      done: 'Nákupný asistent prezretý',
      failed: 'Nákupného asistenta sa nepodarilo prezrieť',
    },
    storefront_conversations: {
      running: 'Čítam konverzácie so zákazníkmi',
      done: 'Konverzácie so zákazníkmi prečítané',
      failed: 'Konverzácie so zákazníkmi sa nepodarilo prečítať',
    },
    read_knowledge_page: {
      running: 'Čítam stránku znalostí',
      done: 'Stránka znalostí prečítaná',
      failed: 'Stránku znalostí sa nepodarilo prečítať',
      counted:
        'one:Prečítaná {count} stránka znalostí|few:Prečítané {count} stránky znalostí|many:Prečítanej {count} stránky znalostí|other:Prečítaných {count} stránok znalostí',
    },
    search_work_results: {
      running: 'Prehľadávam uložené výsledky',
      done: 'Uložené výsledky prehľadané',
      failed: 'Uložené výsledky sa nepodarilo prehľadať',
    },
    read_work_result: {
      running: 'Čítam uložený výsledok',
      done: 'Uložený výsledok prečítaný',
      failed: 'Uložený výsledok sa nepodarilo prečítať',
      counted:
        'one:Prečítaný {count} uložený výsledok|few:Prečítané {count} uložené výsledky|many:Prečítaného {count} uloženého výsledku|other:Prečítaných {count} uložených výsledkov',
    },
    read_previous_chat: {
      running: 'Čítam predchádzajúci chat',
      done: 'Predchádzajúci chat prečítaný',
      failed: 'Predchádzajúci chat sa nepodarilo prečítať',
      counted:
        'one:Prečítaný {count} predchádzajúci chat|few:Prečítané {count} predchádzajúce chaty|many:Prečítaného {count} predchádzajúceho chatu|other:Prečítaných {count} predchádzajúcich chatov',
    },
    read_connected_document: {
      running: 'Čítam pripojený dokument',
      done: 'Pripojený dokument prečítaný',
      failed: 'Pripojený dokument sa nepodarilo prečítať',
      counted:
        'one:Prečítaný {count} pripojený dokument|few:Prečítané {count} pripojené dokumenty|many:Prečítaného {count} pripojeného dokumentu|other:Prečítaných {count} pripojených dokumentov',
    },
    read_url: {
      running: 'Čítam webovú stránku',
      done: 'Webová stránka prečítaná',
      failed: 'Webovú stránku sa nepodarilo prečítať',
      counted:
        'one:Prečítaná {count} webová stránka|few:Prečítané {count} webové stránky|many:Prečítanej {count} webovej stránky|other:Prečítaných {count} webových stránok',
    },
    open_web: {
      running: 'Čítam webovú stránku',
      done: 'Webová stránka prečítaná',
      failed: 'Webovú stránku sa nepodarilo prečítať',
      counted:
        'one:Prečítaná {count} webová stránka|few:Prečítané {count} webové stránky|many:Prečítanej {count} webovej stránky|other:Prečítaných {count} webových stránok',
    },
    view_image: {
      running: 'Prezerám obrázok',
      done: 'Obrázok prezretý',
      failed: 'Obrázok sa nepodarilo prezrieť',
      counted:
        'one:Prezretý {count} obrázok|few:Prezreté {count} obrázky|many:Prezretého {count} obrázka|other:Prezretých {count} obrázkov',
    },
    read_company_skill: {
      running: 'Kontrolujem Skilly',
      done: 'Skilly skontrolované',
      failed: 'Skilly sa nepodarilo skontrolovať',
    },
    find_right_skill: {
      running: 'Hľadám vhodné Skilly',
      done: 'Skilly nájdené',
      failed: 'Skilly sa nepodarilo nájsť',
    },
    list_company_skills: {
      running: 'Kontrolujem Skilly',
      done: 'Skilly skontrolované',
      failed: 'Skilly sa nepodarilo skontrolovať',
    },
    read_my_settings: {
      running: 'Kontrolujem vaše nastavenia',
      done: 'Vaše nastavenia skontrolované',
      failed: 'Vaše nastavenia sa nepodarilo skontrolovať',
    },
    read_account_overview: {
      running: 'Kontrolujem váš pracovný priestor',
      done: 'Pracovný priestor skontrolovaný',
      failed: 'Pracovný priestor sa nepodarilo skontrolovať',
    },
    list_team: {
      running: 'Kontrolujem tím',
      done: 'Tím skontrolovaný',
      failed: 'Tím sa nepodarilo skontrolovať',
    },
    read_company_usage: {
      running: 'Kontrolujem využitie',
      done: 'Využitie skontrolované',
      failed: 'Využitie sa nepodarilo skontrolovať',
    },
    list_connection_tools: {
      running: 'Kontrolujem pripojené aplikácie',
      done: 'Pripojené aplikácie skontrolované',
      failed: 'Pripojené aplikácie sa nepodarilo skontrolovať',
    },
    read_connection_action: {
      running: 'Kontrolujem pripojené aplikácie',
      done: 'Pripojené aplikácie skontrolované',
      failed: 'Pripojené aplikácie sa nepodarilo skontrolovať',
    },
    list_custom_mcp_tools: {
      running: 'Kontrolujem pripojené aplikácie',
      done: 'Pripojené aplikácie skontrolované',
      failed: 'Pripojené aplikácie sa nepodarilo skontrolovať',
    },
    read_custom_mcp_action: {
      running: 'Kontrolujem pripojené aplikácie',
      done: 'Pripojené aplikácie skontrolované',
      failed: 'Pripojené aplikácie sa nepodarilo skontrolovať',
    },
  },
  sl: {
    // Projects v1.
    remove_from_project: {
      running: 'Odstranjujem iz projekta',
      done: 'Odstranjeno iz projekta',
      failed: 'Iz projekta ni bilo mogoče odstraniti',
    },
    project_context: {
      running: 'Berem opis projekta',
      done: 'Opis projekta prebran',
      failed: 'Opisa projekta ni bilo mogoče prebrati',
    },
    read_projects: {
      running: 'Preverjam projekte',
      done: 'Projekti preverjeni',
      failed: 'Projektov ni bilo mogoče preveriti',
    },
    draft_project: {
      running: 'Pripravljam osnutek projekta',
      done: 'Osnutek projekta pripravljen',
      failed: 'Osnutka projekta ni bilo mogoče pripraviti',
    },
    update_project: {
      running: 'Pripravljam spremembo projekta',
      done: 'Sprememba projekta pripravljena',
      failed: 'Spremembe projekta ni bilo mogoče pripraviti',
    },
    move_chat_to_project: {
      running: 'Premikam klepet',
      done: 'Klepet premaknjen',
      failed: 'Klepeta ni bilo mogoče premakniti',
    },
    add_to_project: {
      running: 'Dodajam v projekt',
      done: 'Dodano v projekt',
      failed: 'V projekt ni bilo mogoče dodati',
    },
    suggest_project_move: {
      running: 'Preverjam, kam spada klepet',
      done: 'Predlagan projekt',
      failed: 'Projekta ni bilo mogoče predlagati',
    },
    search_company_knowledge: {
      running: 'Iskanje po znanju podjetja',
      done: 'Znanje podjetja preiskano',
      failed: 'Po znanju podjetja ni bilo mogoče iskati',
    },
    read_company_context: {
      running: 'Branje konteksta podjetja',
      done: 'Kontekst podjetja prebran',
      failed: 'Konteksta podjetja ni bilo mogoče prebrati',
    },
    find_company_work: {
      running: 'Pregledovanje dela podjetja',
      done: 'Delo podjetja pregledano',
      failed: 'Dela podjetja ni bilo mogoče pregledati',
    },
    read_company_meeting: {
      running: 'Branje sestanka',
      done: 'Sestanek prebran',
      failed: 'Sestanka ni bilo mogoče prebrati',
      counted:
        'one:Prebran {count} sestanek|two:Prebrana {count} sestanka|few:Prebrani {count} sestanki|other:Prebranih {count} sestankov',
    },
    read_company_file: {
      running: 'Branje datoteke',
      done: 'Datoteka prebrana',
      failed: 'Datoteke ni bilo mogoče prebrati',
      counted:
        'one:Prebrana {count} datoteka|two:Prebrani {count} datoteki|few:Prebrane {count} datoteke|other:Prebranih {count} datotek',
    },
    search_company_file: {
      running: 'Iskanje po datoteki',
      done: 'Datoteka preiskana',
      failed: 'Po datoteki ni bilo mogoče iskati',
      counted:
        'one:Preiskana {count} datoteka|two:Preiskani {count} datoteki|few:Preiskane {count} datoteke|other:Preiskanih {count} datotek',
    },
    read_company_source: {
      running: 'Branje dokumenta',
      done: 'Dokument prebran',
      failed: 'Dokumenta ni bilo mogoče prebrati',
      counted:
        'one:Prebran {count} dokument|two:Prebrana {count} dokumenta|few:Prebrani {count} dokumenti|other:Prebranih {count} dokumentov',
    },
    storefront_stores: {
      running: 'Preverjanje nakupovalnih pomočnikov',
      done: 'Nakupovalni pomočniki preverjeni',
      failed: 'Nakupovalnih pomočnikov ni bilo mogoče preveriti',
    },
    render_storefront_preview: {
      running: 'Ogled nakupovalnega pomočnika',
      done: 'Nakupovalni pomočnik ogledan',
      failed: 'Nakupovalnega pomočnika ni bilo mogoče ogledati',
    },
    storefront_conversations: {
      running: 'Branje pogovorov s kupci',
      done: 'Pogovori s kupci prebrani',
      failed: 'Pogovorov s kupci ni bilo mogoče prebrati',
    },
    read_knowledge_page: {
      running: 'Branje strani znanja',
      done: 'Stran znanja prebrana',
      failed: 'Strani znanja ni bilo mogoče prebrati',
      counted:
        'one:Prebrana {count} stran znanja|two:Prebrani {count} strani znanja|few:Prebrane {count} strani znanja|other:Prebranih {count} strani znanja',
    },
    search_work_results: {
      running: 'Iskanje po shranjenih rezultatih',
      done: 'Shranjeni rezultati preiskani',
      failed: 'Po shranjenih rezultatih ni bilo mogoče iskati',
    },
    read_work_result: {
      running: 'Branje shranjenega rezultata',
      done: 'Shranjeni rezultat prebran',
      failed: 'Shranjenega rezultata ni bilo mogoče prebrati',
      counted:
        'one:Prebran {count} shranjeni rezultat|two:Prebrana {count} shranjena rezultata|few:Prebrani {count} shranjeni rezultati|other:Prebranih {count} shranjenih rezultatov',
    },
    read_previous_chat: {
      running: 'Branje prejšnjega klepeta',
      done: 'Prejšnji klepet prebran',
      failed: 'Prejšnjega klepeta ni bilo mogoče prebrati',
      counted:
        'one:Prebran {count} prejšnji klepet|two:Prebrana {count} prejšnja klepeta|few:Prebrani {count} prejšnji klepeti|other:Prebranih {count} prejšnjih klepetov',
    },
    read_connected_document: {
      running: 'Branje povezanega dokumenta',
      done: 'Povezani dokument prebran',
      failed: 'Povezanega dokumenta ni bilo mogoče prebrati',
      counted:
        'one:Prebran {count} povezani dokument|two:Prebrana {count} povezana dokumenta|few:Prebrani {count} povezani dokumenti|other:Prebranih {count} povezanih dokumentov',
    },
    read_url: {
      running: 'Branje spletne strani',
      done: 'Spletna stran prebrana',
      failed: 'Spletne strani ni bilo mogoče prebrati',
      counted:
        'one:Prebrana {count} spletna stran|two:Prebrani {count} spletni strani|few:Prebrane {count} spletne strani|other:Prebranih {count} spletnih strani',
    },
    open_web: {
      running: 'Branje spletne strani',
      done: 'Spletna stran prebrana',
      failed: 'Spletne strani ni bilo mogoče prebrati',
      counted:
        'one:Prebrana {count} spletna stran|two:Prebrani {count} spletni strani|few:Prebrane {count} spletne strani|other:Prebranih {count} spletnih strani',
    },
    view_image: {
      running: 'Ogledovanje slike',
      done: 'Slika ogledana',
      failed: 'Slike ni bilo mogoče ogledati',
      counted:
        'one:Ogledana {count} slika|two:Ogledani {count} sliki|few:Ogledane {count} slike|other:Ogledanih {count} slik',
    },
    read_company_skill: {
      running: 'Preverjanje Skillov',
      done: 'Skilli preverjeni',
      failed: 'Skillov ni bilo mogoče preveriti',
    },
    find_right_skill: {
      running: 'Iskanje pravih Skillov',
      done: 'Skilli najdeni',
      failed: 'Skillov ni bilo mogoče najti',
    },
    list_company_skills: {
      running: 'Preverjanje Skillov',
      done: 'Skilli preverjeni',
      failed: 'Skillov ni bilo mogoče preveriti',
    },
    read_my_settings: {
      running: 'Preverjanje vaših nastavitev',
      done: 'Vaše nastavitve preverjene',
      failed: 'Vaših nastavitev ni bilo mogoče preveriti',
    },
    read_account_overview: {
      running: 'Preverjanje vašega delovnega prostora',
      done: 'Delovni prostor preverjen',
      failed: 'Delovnega prostora ni bilo mogoče preveriti',
    },
    list_team: {
      running: 'Preverjanje ekipe',
      done: 'Ekipa preverjena',
      failed: 'Ekipe ni bilo mogoče preveriti',
    },
    read_company_usage: {
      running: 'Preverjanje porabe',
      done: 'Poraba preverjena',
      failed: 'Porabe ni bilo mogoče preveriti',
    },
    list_connection_tools: {
      running: 'Preverjanje povezanih aplikacij',
      done: 'Povezane aplikacije preverjene',
      failed: 'Povezanih aplikacij ni bilo mogoče preveriti',
    },
    read_connection_action: {
      running: 'Preverjanje povezanih aplikacij',
      done: 'Povezane aplikacije preverjene',
      failed: 'Povezanih aplikacij ni bilo mogoče preveriti',
    },
    list_custom_mcp_tools: {
      running: 'Preverjanje povezanih aplikacij',
      done: 'Povezane aplikacije preverjene',
      failed: 'Povezanih aplikacij ni bilo mogoče preveriti',
    },
    read_custom_mcp_action: {
      running: 'Preverjanje povezanih aplikacij',
      done: 'Povezane aplikacije preverjene',
      failed: 'Povezanih aplikacij ni bilo mogoče preveriti',
    },
  },
  sv: {
    // Projects v1.
    remove_from_project: {
      running: 'Tar ut det ur projektet',
      done: 'Tog ut det ur projektet',
      failed: 'Kunde inte ta ut det ur projektet',
    },
    project_context: {
      running: 'Läser projektets brief',
      done: 'Läste projektets brief',
      failed: 'Kunde inte läsa projektets brief',
    },
    read_projects: {
      running: 'Går igenom projekt',
      done: 'Gick igenom projekt',
      failed: 'Kunde inte gå igenom projekt',
    },
    draft_project: {
      running: 'Gör ett utkast till projektet',
      done: 'Gjorde ett utkast till projektet',
      failed: 'Kunde inte göra ett utkast till projektet',
    },
    update_project: {
      running: 'Förbereder ändringen av projektet',
      done: 'Förberedde ändringen av projektet',
      failed: 'Kunde inte förbereda ändringen av projektet',
    },
    move_chat_to_project: {
      running: 'Flyttar chatten',
      done: 'Flyttade chatten',
      failed: 'Kunde inte flytta chatten',
    },
    add_to_project: {
      running: 'Lägger till i projektet',
      done: 'Lade till i projektet',
      failed: 'Kunde inte lägga till i projektet',
    },
    suggest_project_move: {
      running: 'Kollar var chatten hör hemma',
      done: 'Föreslog ett projekt',
      failed: 'Kunde inte föreslå ett projekt',
    },
    search_company_knowledge: {
      running: 'Söker i företagskunskap',
      done: 'Sökte i företagskunskap',
      failed: 'Kunde inte söka i företagskunskap',
    },
    read_company_context: {
      running: 'Läser företagskontext',
      done: 'Läste företagskontext',
      failed: 'Kunde inte läsa företagskontext',
    },
    find_company_work: {
      running: 'Går igenom företagets arbete',
      done: 'Gick igenom företagets arbete',
      failed: 'Kunde inte gå igenom företagets arbete',
    },
    read_company_meeting: {
      running: 'Läser ett möte',
      done: 'Läste ett möte',
      failed: 'Kunde inte läsa ett möte',
      counted: 'one:Läste {count} möte|other:Läste {count} möten',
    },
    read_company_file: {
      running: 'Läser en fil',
      done: 'Läste en fil',
      failed: 'Kunde inte läsa en fil',
      counted: 'one:Läste {count} fil|other:Läste {count} filer',
    },
    search_company_file: {
      running: 'Söker i en fil',
      done: 'Sökte i en fil',
      failed: 'Kunde inte söka i en fil',
      counted: 'one:Sökte i {count} fil|other:Sökte i {count} filer',
    },
    read_company_source: {
      running: 'Läser ett dokument',
      done: 'Läste ett dokument',
      failed: 'Kunde inte läsa ett dokument',
      counted: 'one:Läste {count} dokument|other:Läste {count} dokument',
    },
    storefront_stores: {
      running: 'Kollar era shoppingassistenter',
      done: 'Kollade era shoppingassistenter',
      failed: 'Kunde inte kolla era shoppingassistenter',
    },
    render_storefront_preview: {
      running: 'Tittar på shoppingassistenten',
      done: 'Tittade på shoppingassistenten',
      failed: 'Kunde inte titta på shoppingassistenten',
    },
    storefront_conversations: {
      running: 'Läser kundernas samtal',
      done: 'Läste kundernas samtal',
      failed: 'Kunde inte läsa kundernas samtal',
    },
    read_knowledge_page: {
      running: 'Läser en kunskapssida',
      done: 'Läste en kunskapssida',
      failed: 'Kunde inte läsa en kunskapssida',
      counted: 'one:Läste {count} kunskapssida|other:Läste {count} kunskapssidor',
    },
    search_work_results: {
      running: 'Söker i sparade resultat',
      done: 'Sökte i sparade resultat',
      failed: 'Kunde inte söka i sparade resultat',
    },
    read_work_result: {
      running: 'Läser ett sparat resultat',
      done: 'Läste ett sparat resultat',
      failed: 'Kunde inte läsa ett sparat resultat',
      counted: 'one:Läste {count} sparat resultat|other:Läste {count} sparade resultat',
    },
    read_previous_chat: {
      running: 'Läser en tidigare chatt',
      done: 'Läste en tidigare chatt',
      failed: 'Kunde inte läsa en tidigare chatt',
      counted: 'one:Läste {count} tidigare chatt|other:Läste {count} tidigare chattar',
    },
    read_connected_document: {
      running: 'Läser ett anslutet dokument',
      done: 'Läste ett anslutet dokument',
      failed: 'Kunde inte läsa ett anslutet dokument',
      counted: 'one:Läste {count} anslutet dokument|other:Läste {count} anslutna dokument',
    },
    read_url: {
      running: 'Läser en webbsida',
      done: 'Läste en webbsida',
      failed: 'Kunde inte läsa en webbsida',
      counted: 'one:Läste {count} webbsida|other:Läste {count} webbsidor',
    },
    open_web: {
      running: 'Läser en webbsida',
      done: 'Läste en webbsida',
      failed: 'Kunde inte läsa en webbsida',
      counted: 'one:Läste {count} webbsida|other:Läste {count} webbsidor',
    },
    view_image: {
      running: 'Tittar på en bild',
      done: 'Tittade på en bild',
      failed: 'Kunde inte titta på en bild',
      counted: 'one:Tittade på {count} bild|other:Tittade på {count} bilder',
    },
    read_company_skill: {
      running: 'Kontrollerar Skills',
      done: 'Kontrollerade Skills',
      failed: 'Kunde inte kontrollera Skills',
    },
    find_right_skill: {
      running: 'Letar efter rätt Skills',
      done: 'Hittade Skills',
      failed: 'Kunde inte hitta Skills',
    },
    list_company_skills: {
      running: 'Kontrollerar Skills',
      done: 'Kontrollerade Skills',
      failed: 'Kunde inte kontrollera Skills',
    },
    read_my_settings: {
      running: 'Kontrollerar dina inställningar',
      done: 'Kontrollerade dina inställningar',
      failed: 'Kunde inte kontrollera dina inställningar',
    },
    read_account_overview: {
      running: 'Kontrollerar din arbetsyta',
      done: 'Kontrollerade din arbetsyta',
      failed: 'Kunde inte kontrollera din arbetsyta',
    },
    list_team: {
      running: 'Kontrollerar teamet',
      done: 'Kontrollerade teamet',
      failed: 'Kunde inte kontrollera teamet',
    },
    read_company_usage: {
      running: 'Kontrollerar användningen',
      done: 'Kontrollerade användningen',
      failed: 'Kunde inte kontrollera användningen',
    },
    list_connection_tools: {
      running: 'Kontrollerar anslutna appar',
      done: 'Kontrollerade anslutna appar',
      failed: 'Kunde inte kontrollera anslutna appar',
    },
    read_connection_action: {
      running: 'Kontrollerar anslutna appar',
      done: 'Kontrollerade anslutna appar',
      failed: 'Kunde inte kontrollera anslutna appar',
    },
    list_custom_mcp_tools: {
      running: 'Kontrollerar anslutna appar',
      done: 'Kontrollerade anslutna appar',
      failed: 'Kunde inte kontrollera anslutna appar',
    },
    read_custom_mcp_action: {
      running: 'Kontrollerar anslutna appar',
      done: 'Kontrollerade anslutna appar',
      failed: 'Kunde inte kontrollera anslutna appar',
    },
  },
};
